// Venice, "Mist over the Grand Canal". A drizzly night in late October, about eleven, on the Riva del Vin (San Polo,
// 45.4374 N, 12.3356 E), the stone quay just south of the Rialto Bridge where the Grand Canal bends: we stand by the
// last bacaro still open, at the edge of the quay, and look north-east up the canal at the bridge. What is really
// there, left to right: the bacaro's corner and its awning on our quay, the Gothic palazzi of the San Polo bank, the
// campanile of San Giovanni Elemosinario behind the Rialto market, the Palazzo dei Camerlenghi at the bridge's west
// foot, the Rialto Bridge (one white Istrian stone arch carrying two rows of shops under stepped roofs and a central
// portico with a pediment), the Fondaco dei Tedeschi with its merlons at the east foot (an altana on its roof carries
// the city's billboard), and a red Gothic palazzo of the Riva del Carbon, with the gondola station (stazio) at our
// end of the quay. Through the arch the canal runs on, past far palazzi, towards Ca' d'Oro.
// Compressed, not rearranged: the bend lets us look straight across the water at the bridge, the far banks are
// pulled together, and the bridge is drawn a little larger than it would look from 150 m.
// Back to front: sky, a moon in the mist, drifting overcast -> far drizzle -> far palazzi up the canal -> the
// campanile, the Camerlenghi, the Fondaco and its altana -> the bridge between them (arcades, walkers, balustrade,
// arch; its ramps run in behind the two palazzi) -> the
// water (mirror, ripples, lamp streaks, rain rings) -> the near palazzi on both banks and their mirror -> mist on
// the water -> the vaporetto, the gondola -> mid drizzle -> the quay: the bacaro and its awning, lamps, the stazio,
// people, the cat, pigeons -> near drizzle.
// render(t, { crowd }) thins the people; crowd 0 draws nobody at all (see CROWD).
//
// The loop's timeline (seconds; every period divides 240; see the constants under "Timed events"):
//   event                                              time                       sound
//   the late couple at the bacaro finish and leave     30 (walk off 30 - 48)
//   a gondola rows down the canal, right to left       4 - 70 (a couple aboard)
//   the vaporetto crosses, left to right               18 - 58                   horn at 18, boat 18 - 58
//   the waiter stacks their two chairs                 34 - 62, wipes the table to 74
//   someone with an umbrella walks the quay            44 - 84, back 184 - 224
//   a gull glides down onto a mooring pole             66 - 74 (and leaves 228 - 238) gull at 70
//   the reader at table B pays and leaves              80 (walks off 80 - 104)
//   the waiter stacks his chair                        84 - 102
//   the campanile strikes three strokes                96, 100, 104               bell; pigeons lift off the roofs 96 - 140
//   the waiter sweeps under the awning                 112 - 150
//   a gondola rows back up, left to right, a lantern   116 - 186
//   the cat walks along the quay edge                  124 - 142, back 226 - 236
//   two friends under one umbrella stroll by           100 - 146
//   the vaporetto comes back, right to left            138 - 178                 horn at 138, boat 138 - 178
//   the couple walks back in; he sets two chairs       150 - 178, they sit at 180
//   he brings them two spritz                          186 - 198
//   the reader comes back, gets his chair, sits        200 - 220 (the chair at 208 - 219)
//   gulls cross the sky                                30 - 44, 206 - 220         gull at 34, 210
//   the bridge couple kiss                             every 40 s (16, 56 ...)
//   the quay lamp by the bridge sputters               66 - 67.5, 172 - 173; the sign's lamp 88, 201
//   small things all loop long: walkers on the bridge, windows switching, the gondolier at the stazio calling out
//   every 30 s, the drizzle's rings, the mist, the laundry on the altana, the bacaro's bulbs.
// Longest gap between two starts: 16 s (228 -> 4 across the wrap); the kisses (16, 56, 96 ...) fall between.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, cycle, scroll, hash, rng, bayer, hex,
  makeCanvas, makeHalo, drawHalo, text, textWidth, reflect, streak, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                              // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const WL = 156;                                       // the far waterline: the bridge, the Camerlenghi, the Fondaco
const NWL = 172;                                      // the near palazzi's waterline, on both banks
const RIVA = 206;                                     // top of our quay's stone edge; the water ends just above it
const BR = { cx: 250, x0: 182, x1: 318, crown: 128, deck: 118, foot: 146, slope: 0.27 };   // the Rialto Bridge
const deckY = x => BR.deck + Math.abs(x - BR.cx) * BR.slope;                               // the walkway's line
const ARCH_R = (68 * 68 + 28 * 28) / 56, ARCH_CY = BR.crown + ARCH_R;                       // the segmental arch
const archY = x => ARCH_CY - Math.sqrt(Math.max(0, ARCH_R * ARCH_R - (x - BR.cx) ** 2));
const PORT = { x0: 238, x1: 262, top: 95, peak: 84 };  // the central portico and its pediment
const CAMER = { x0: 94, x1: 150, top: 100 };          // the Palazzo dei Camerlenghi
const FOND = { x0: 352, x1: 420, top: 97 };           // the Fondaco dei Tedeschi
const CAMP = { x0: 106, x1: 114, top: 58, tip: 48 };  // the campanile of San Giovanni Elemosinario
const BILL = { x: 358, y: 66, w: 48, h: 20 };         // the sign on the Fondaco's altana (the site's pitch is painted here)
const BILL_LAMPS = [BILL.x + 10, BILL.x + 38], BILL_LAMP_Y = BILL.y - 4;
const MOON = { x: 152, y: 34 };
const LAMPS = [178, 338];                             // the quay's lamp posts (x of the post)
const LAMP_Y = 172;                                   // row of their lanterns
const BAR = { x1: 38, door: 12, dw: 14 };             // the bacaro's corner: its facade ends at x1
const AWN = { x0: 38, x1: 146, y: 180 };              // the awning: valance top row
const TABLES = [{ x: 66 }, { x: 116 }];               // table A (the couple), table B (the reader)
const TROW = 236;                                     // feet of the tables and the people sitting at them
const STACK_X = 46;                                   // where the waiter stacks the chairs, by the wall
const STAZIO = { x0: 372 };                           // the gondola station: moored gondolas from here to the right
const EDGE = 224, WALK = 248, NEAR = 262;             // feet rows: at the quay's edge, the walkers' lane, the nearest

// The share of the people the scene shows by default; render(t, { crowd }) overrides it for one frame. Everyone has a
// fixed rank in [0, 1) and is drawn while rank < crowd; rank 0 (the waiter, the late couple, the rowing gondoliers and
// their passengers) stays while anybody does. Crowd 0 is nobody at all: the cast goes, with the rowed gondolas, the
// spritz, the broom and the newspaper; the chairs stand where they were left, the vaporetto still passes, empty.
const CROWD = 0.7;
const here = (rank, crowd) => crowd > 0 && (rank === 0 || rank < crowd);

/* ================= Timed events (loop seconds) ================= */
const GONDOLAS = [{ t0: 4, t1: 70, dir: -1, lantern: false }, { t0: 116, t1: 186, dir: 1, lantern: true }];
const VAPS = [{ t0: 18, t1: 58, dir: 1 }, { t0: 138, t1: 178, dir: -1 }];
const BELLS = [96, 100, 104];                         // the campanile
const FLOCK_T0 = 96, FLOCK_T1 = 140;                  // the roofs' pigeons start up on the first stroke and settle again
const CAT_WALK = [[124, 142, 1], [226, 236, -1]];
const GULL_DROP = [66, 74];                           // a gull comes down onto a pole of the stazio
const GULL_PASS = [[30, 44, 1], [206, 220, -1]];      // a pair of gulls crossing the sky
const KISS_EVERY = 40;                                // the couple on the bridge
const UMBRELLA = [[44, 84, 1], [184, 224, -1]];       // someone with an umbrella walks the quay
const FRIENDS = [100, 146];                           // two friends under one umbrella, left to right
const LAMP_FLICK = [[66, 67.5], [172, 173]];          // the lamp by the bridge sputters
const CALL_EVERY = 30;                                // the gondolier at the stazio calls out to the quay
// the bacaro's little machine: who sits where, and where the waiter is (see waiterAt)
const COUPLE = { leave: 30, gone: 48, arrive: 150, at: 166, sit: 180 };
const READER = { leave: 80, gone: 104, arrive: 200, at: 212, sit: 220 };
const SPRITZ = [186, 198];

/* ================= Pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const MIXED = {};
function mixHex(a, b, t) {
  const k = a + b + t;
  if (!MIXED[k]) { const A = C(a), B = C(b); MIXED[k] = toHex(...A.map((v, i) => Math.round(lerp(v, B[i], t)))); }
  return MIXED[k];
}
/** ImageData-backed painter for prerendering */
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    clear(i, j) { if (i >= 0 && j >= 0 && i < w && j < h) d[(j * w + i) * 4 + 3] = 0; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    line(x0, y0, x1, y1, col) {
      const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
      for (let k = 0; k <= n; k++) this.set(lerp(x0, x1, k / n), lerp(y0, y1, k / n), col);
    },
    text(s, i, j, col) { x.putImageData(img, 0, 0); text(x, s, i, j, col); d.set(x.getImageData(0, 0, w, h).data); },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 (soft < 1 narrows the dithered band) */
function pick(ramp, v, i, j, soft = 1) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * soft) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only) */
function vnoise(x, y, s) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const a = hash(xi, yi, s), b = hash(xi + 1, yi, s), c = hash(xi, yi + 1, s), d = hash(xi + 1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** crop a painter to its opaque bounding box (build time); draw the result with blit() */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = pix(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
const blit = (o, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(o.c, o.x, o.y); ctx.globalAlpha = 1; };
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
const dot = (x, y, c) => { ctx.fillStyle = c; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); };
const inWin = (t, [a, b]) => t >= a && t < b;
function flick(t, list, id) {                         // a lamp that sputters inside the given windows
  for (const [a, b] of list) if (t >= a && t < b) { const hv = hash(id, Math.floor(t * 20)); return hv > 0.45 ? 1 : hv > 0.2 ? 0.45 : 0.12; }
  return 1;
}
/** rows of a pointed (Venetian Gothic) window w px wide: [x offset, width] per row from the top */
function pointed(w, h) {
  const rows = [];
  for (let j = 0; j < h; j++) {
    if (w >= 3 && j === 0) rows.push(w & 1 ? [w >> 1, 1] : [(w >> 1) - 1, 2]);
    else if (w >= 4 && j === 1) rows.push([1, w - 2]);
    else rows.push([0, w]);
  }
  return rows;
}
/** rows of a round-headed window */
const rounded = (w, h) => Array.from({ length: h }, (_, j) => (j === 0 && w >= 3 ? [1, w - 2] : [0, w]));

/* ================= Sky: a misty autumn night, the city's glow low down ================= */
const SKY = ['#070b1c', '#0a1024', '#0e152c', '#121b34', '#18223c', '#1f2944', '#28314a', '#33394e', '#403f50',
  '#4e4650', '#5e4e50', '#6e5850'];
const SKY_H = 160;
function skyV(i, y) {
  let v = Math.pow(Math.min(1, y / 150), 1.5) * 0.72;                                    // dark overhead, glowing low down
  v += Math.max(0, 1 - Math.hypot((i - BR.cx) / 260, (y - 140) / 60)) ** 1.6 * 0.3;      // the city's warmth over the bridge
  v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 90, (y - MOON.y) / 50)) ** 2 * 0.24;     // the moon through the mist
  return v;
}
function renderSky() {
  const p = pix(W, SKY_H);
  for (let y = 0; y < SKY_H; y++) for (let i = 0; i < W; i++) p.set(i, y, pick(SKY, skyV(i, y), i, y));
  return p.done();
}
const STARS = [];
{
  const r = rng(12);
  while (STARS.length < 30) {
    const x = Math.floor(r() * W), y = 2 + Math.floor(r() * 60);
    if (skyV(x, y) > 0.2 || Math.hypot(x - MOON.x, y - MOON.y) < 24) continue;
    STARS.push({ x, y, k: 24 + Math.floor(r() * 60), ph: r(), b: 0.3 + r() * 0.5 });
  }
}
/** a waning gibbous moon, soft-edged in the mist */
function renderMoon() {
  const r = 6, s = 15, p = pix(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - 7, dy = y - 7, dd = Math.hypot(dx, dy);
    if (dd > r + 0.3) continue;
    const sh = Math.hypot(dx + 8.5, dy + 1);                           // the shadow eats the left edge
    if (sh < r + 0.6) { if (dd > r - 1) p.set(i, y, '#58607a', 120); continue; }
    const m = vnoise(i / 2.4, y / 2.4, 7) > 0.62;                       // the maria
    p.set(i, y, dd > r - 0.8 ? '#d8d2c0' : m ? '#d6d0be' : '#f2ecda');
  }
  return p.done();
}

/* ================= Low overcast (tileable, drifting; lit from below by the city) ================= */
const CLOUD_DEFS = [
  { seed: 17, y: -8, h: 50, banks: 5, k: 1, rx: [20, 44], ry: [4, 8], cut: 0.35,
    pal: { top: '#1c2440', body: '#161e38', core: '#141a32', bot: '#262c46', bot2: '#1e2640' } },
  { seed: 31, y: 22, h: 52, banks: 5, k: 2, rx: [20, 40], ry: [3.5, 7], cut: 0.35,
    pal: { top: '#2a3048', body: '#222a42', core: '#1e263e', bot: '#3e3e52', bot2: '#30344a' } },
  { seed: 47, y: 58, h: 40, banks: 4, k: 1, rx: [24, 50], ry: [2.5, 5], cut: 0.35,
    pal: { top: '#3a3a50', body: '#30324a', core: '#2c2e46', bot: '#5a4c52', bot2: '#463e4e' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = (b + r() * 0.8) * W / def.banks, bw = 70 + r() * 110, base = h * (0.14 + ((b * 2 + 1) % def.banks + 0.2 + r() * 0.6) / def.banks * 0.74);
    const np = 5 + Math.floor(r() * 5);
    for (let i = 0; i < np; i++) {
      const rx = lerp(def.rx[0], def.rx[1], r()), ry = lerp(def.ry[0], def.ry[1], r());
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.4, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const q of puffs) {
      let dx = x - q.x; dx -= Math.round(dx / W) * W;
      const dy = y - q.y;
      let v = 1 - (dx * dx) / (q.rx * q.rx) - (dy * dy) / (q.ry * q.ry);
      if (y > q.base) v -= (y - q.base) * def.cut;
      if (v > best) best = v;
    }
    best += 0.2 * Math.sin(TAU * 13 * x / W + y * 0.6 + def.seed) * Math.sin(TAU * 7 * x / W - y * 0.33);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = pix(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.36, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 2) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    p.set(x, y, col);
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= Mist (tileable, drifting, breathing) ================= */
const MIST_DEFS = [
  { seed: 5, y: 94, h: 46, k: 1, dir: 1, top: '#5a5a70', bot: '#7a7486', alpha: 0.5 },     // over the far roofs
  { seed: 9, y: 136, h: 46, k: 2, dir: -1, top: '#70707e', bot: '#948c94', alpha: 0.55 },    // low over the water
  { seed: 13, y: 170, h: 38, k: 1, dir: 1, top: '#56606a', bot: '#6a7078', alpha: 0.3 },    // drifting in front of the near palazzi
];
function makeMist(def) {
  const h = def.h, p = pix(W, h), A = C(def.top), B = C(def.bot);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const v = y / (h - 1), prof = Math.pow(Math.sin(Math.PI * Math.pow(v, 0.8)), 1.4);
    const nz = 0.5 + 0.3 * Math.sin(TAU * 2 * x / W + def.seed + y * 0.06) + 0.18 * Math.sin(TAU * 5 * x / W - y * 0.21 + def.seed * 2)
      + 0.12 * Math.sin(TAU * 11 * x / W + y * 0.43) + 0.08 * Math.sin(TAU * 23 * x / W - y * 0.7);
    const q = Math.floor(Math.min(1, prof * clamp(nz, 0, 1) * 1.1) * 4 + bayer(x, y)) / 4;
    if (q <= 0) continue;
    p.set(x, y, [lerp(A[0], B[0], v), lerp(A[1], B[1], v), lerp(A[2], B[2], v)], Math.round(Math.min(1, q) * 255));
  }
  return { ...def, c: p.done() };
}

/* ================= The city's materials ================= */
const ISTRIA = ['#2e2c3c', '#48444e', '#686064', '#8c8078', '#b0a18c', '#d2c0a0', '#ecdcb8', '#fff2d2'];   // Istrian stone: shadow to floodlit
const ROSE = ['#2a1e2c', '#3e2832', '#563440', '#704448', '#8a5450', '#a46658'];                          // rose plaster at night
const OCHRE = ['#262230', '#383038', '#4e4240', '#68564a', '#826a52', '#9c805c'];
const BRICK = ['#22161e', '#341e24', '#4a2a2c', '#623632', '#7a443a', '#94543e'];                       // red Gothic plaster
const MIST_STONE = ['#181c2a', '#202434', '#2a2c3c', '#343446', '#403e50', '#4c4858'];                  // far palazzi in the mist
const ROOF = ['#1e1a26', '#2a222c', '#3a2c30', '#4a3634', '#5c443a'];                                   // coppi at night
const LEAD = ['#22283a', '#2e3446', '#3c4254', '#4c5264', '#606476'];                                   // the bridge's roofs
const GLASS = '#121424', SHUT = '#24382e', SHUT_L = '#2e4a3a';
const LIT = ['#ffd98c', '#ffc46a', '#ffe2a8', '#f2b25c', '#ffcf7a'];
const WINS = [];                                      // windows that switch: { id, x, y, w, h, rows, n, p, off, col, layer }
let WIN_LAYER = 0;                                     // 0 far up the canal, 1 the Camerlenghi and the Fondaco, 2 the near palazzi, 3 the bacaro
function addWin(r, x, y, w, h, shape = pointed, p = 0.45) {
  WINS.push({ id: WINS.length, x, y, w, h, rows: shape(w, h), n: 3 + Math.floor(r() * 6), p: p * (0.7 + r() * 0.6), off: r(),
    col: LIT[Math.floor(r() * LIT.length)], layer: WIN_LAYER });
}
/** paint a window's glass (and its stone frame) into a painter */
function glassWin(p, x, y, w, h, shape = pointed, frame = null, glass = GLASS) {
  const rows = shape(w, h);
  rows.forEach(([o, ww], j) => {
    if (frame) { p.set(x + o - 1, y + j, frame); p.set(x + o + ww, y + j, frame); }
    for (let i = 0; i < ww; i++) p.set(x + o + i, y + j, glass);
  });
  if (frame) { const [o, ww] = rows[0]; for (let i = -1; i <= ww; i++) p.set(x + o + i, y - 1, frame); for (let i = -1; i <= w; i++) p.set(x + i, y + h, frame); }
}
/** a bell-shaped Venetian chimney pot (camin a campana) standing on row y */
function chimney(p, x, y, col, lit) {
  p.fill(x, y - 5, 2, 5, col); p.fill(x - 1, y - 7, 4, 2, col); p.fill(x - 2, y - 9, 6, 2, col); p.fill(x - 1, y - 10, 4, 1, lit);
  p.set(x + 2, y - 8, lit);
}

/* ================= Far up the canal: palazzi in the mist beyond the bridge ================= */
const FAR_BASE = 147;
// the canal beyond the bridge, dark teal-green in long ripple bands; FAR_WATER_C is the part seen under the arch, laid
// again over the bridge's floodlight (which would turn it khaki) before the canal mirrors it
const FAR_WATER = ['#050e11', '#071619', '#0b1e21', '#10272a', '#163034', '#1f3c3e'];
const farWater = (i, y) => pick(FAR_WATER, 0.2 + (WL - y) * 0.026 + (vnoise(i / 9, y * 0.9, 33) - 0.5) * 0.42, i, y, 0.6);
const underArch = (i, y) => i > BR.x0 && i < BR.x1 && y > archY(i) + 3;
const FAR_GLINTS = [];                                 // far lights that lie on that water: [x, the window (or -1), colour]
let FAR_WATER_C, ARCH_VEIL_C;
/** a dark teal veil over the canal's mirror of the arch opening: the mirrored far bank and floodlight sink into the water */
function renderArchVeil() {
  const rows = 26, p = pix(W, rows);
  for (let k = 0; k < rows; k++) for (let i = BR.x0 + 1; i < BR.x1; i++) {
    const sy = WL - 1 - Math.floor(k * 1.35);
    if (!underArch(i, sy)) continue;
    const a = 0.5 * (1 - k / (rows + 8));
    if (bayer(i, k) < 0.85) p.set(i, k, '#061418', Math.round(a * 255));
  }
  return trim(p);
}
function renderFar() {
  const p = pix(W, SKY_H), r = rng(77);
  WIN_LAYER = 0;
  // the canal runs away between two rows of palazzi that shrink towards the bend; in the gap, the far lights in the mist
  const TINTS = [MIST_STONE, ['#1e1a2a', '#282232', '#342a3a', '#403244', '#4c3c4c', '#5a4652'], ['#1c1c28', '#262430', '#322e38', '#3e3842', '#4a424a', '#584e52']];
  for (const side of [-1, 1]) {
    let x = side < 0 ? 140 : 362;
    while (side < 0 ? x < 240 : x > 260) {
      const d = Math.abs(x - BR.cx), w = 8 + Math.floor(r() * 8 + d * 0.06), h = 10 + Math.floor(r() * 8 + d * 0.2), base = FAR_BASE - Math.round((110 - d) * 0.04);
      const x0 = side < 0 ? x : x - w, top = base - h, pal = TINTS[Math.floor(r() * 3)], tone = r() * 0.3;
      for (let y = top; y < base; y++) for (let i = x0; i < x0 + w; i++) {
        let v = 0.3 + tone - (y - top) / h * 0.15 + (i === x0 ? 0.15 : 0) - (i === x0 + w - 1 ? 0.12 : 0);
        if (y === top) v += 0.25;
        p.set(i, y, pick(pal, v, i, y, 0.6));
      }
      if (r() < 0.5) chimney(p, x0 + 2 + Math.floor(r() * Math.max(1, w - 5)), top, '#20222e', '#30323e');
      for (let fy = top + 3; fy < base - 4; fy += 5) for (let wx = x0 + 2; wx < x0 + w - 1; wx += 3) {
        p.set(wx, fy, GLASS); p.set(wx, fy + 1, GLASS);
        if (r() < 0.45) addWin(r, wx, fy, 1, 2, rounded, 0.62);
      }
      p.fill(x0 + (w >> 1) - 1, base - 3, 2, 3, '#0c0e16');                             // a water door
      x += side < 0 ? w : -w;
    }
  }
  for (let y = 128; y < FAR_BASE - 4; y++) for (let i = 238; i < 262; i++) if (!p.alpha(i, y)) p.set(i, y, pick(['#161a28', '#1c2232', '#242a3c'], (y - 128) / 22, i, y));   // mist at the bend
  for (let k = 0; k < 6; k++) { const i = 240 + Math.floor(r() * 20), y = 138 + Math.floor(r() * 8); p.set(i, y, '#e8c080'); FAR_GLINTS.push([i, -1, '#e8c080']); }
  for (const w of WINS) if (w.layer === 0 && underArch(w.x, FAR_BASE)) FAR_GLINTS.push([w.x, w.id, w.col]);
  const under = pix(W, WL);                                                        // the canal beyond, below the far banks
  for (let y = FAR_BASE - 4; y < WL; y++) for (let i = 140; i < 362; i++) if (!p.alpha(i, y) || y >= FAR_BASE) {
    const col = farWater(i, y);
    p.set(i, y, col);
    if (underArch(i, y)) under.set(i, y, col);
  }
  FAR_WATER_C = trim(under);
  return p.done();
}

/* ================= The campanile of San Giovanni Elemosinario, over the market roofs ================= */
const BELFRY = [[CAMP.x0 + 2, 62, 1, 7], [CAMP.x0 + 5, 62, 1, 7]];
function drawCampanile(p) {
  const { x0, x1, top, tip } = CAMP, cx = (x0 + x1) >> 1;
  for (let y = top; y < 104; y++) for (let i = x0; i <= x1; i++) {
    let col = pick(BRICK, 0.55 - (i - x0) * 0.05 + (y - top) * 0.004 + (i === x0 ? 0.25 : 0), i, y, 0.6);
    if (y === 60 || y === 71 || y === 72) col = '#a89a8a';                          // stone string courses round the belfry
    if ((i - x0) % 4 === 0 && y > 72 && (y - 72) % 9 < 1) col = '#3a2226';
    p.set(i, y, col);
  }
  for (const [bx, by, bw, bh] of BELFRY) for (let j = 0; j < bh; j++) for (let i = 0; i < bw + 1; i++) p.set(bx + i, by + j, j === 0 && i ? '#3a2a2c' : '#120e16');
  for (let y = tip; y < top; y++) {                                                 // the little pyramid spire
    const hw = Math.floor((y - tip) * 0.5);
    for (let i = -hw; i <= hw; i++) p.set(cx + i, y, i < 0 ? '#5a4a4a' : '#3a2e34');
  }
  p.set(cx, tip - 1, '#c8a86a'); p.set(cx, tip - 2, '#c8a86a'); p.set(cx - 1, tip - 2, '#c8a86a'); p.set(cx + 1, tip - 2, '#c8a86a');
  p.fill(x0 - 1, top, x1 - x0 + 3, 1, '#8a7a72');
}

/* ================= The Palazzo dei Camerlenghi at the bridge's west foot ================= */
function drawCamerlenghi(p, r) {
  const { x0, x1, top } = CAMER;
  WIN_LAYER = 1;
  for (let y = top; y < WL; y++) for (let i = x0; i < x1; i++) {
    const glow = Math.max(0, 1 - Math.abs(i - 150) / 80) * 0.25 + (y - top) / (WL - top) * 0.15;     // floodlit from the bridge side
    let v = 0.3 + glow + (vnoise(i / 3, y / 2, 11) - 0.5) * 0.1;
    if ((i - x0) % 9 === 0) v += 0.14;                                             // pilasters
    if (y === 120 || y === 121 || y === 138 || y === 139) v = 0.8 + glow;            // cornices between the storeys
    if (y === top + 2 || y === top + 3) v = 0.85;
    p.set(i, y, pick(ISTRIA, v, i, y, 0.5));
  }
  for (let i = x0 - 1; i <= x1; i++) { p.set(i, top, '#3a3440'); p.set(i, top + 1, '#d2c0a0'); }
  for (let i = x0; i < x1; i++) for (let y = top - 3; y < top; y++) p.set(i, y, pick(ROOF, 0.4 + (y - top + 3) * 0.1, i, y));
  chimney(p, x0 + 8, top - 3, '#3a2e34', '#5a4a48'); chimney(p, x0 + 40, top - 3, '#3a2e34', '#5a4a48');
  for (let i = x0 + 3; i < x1 - 4; i += 9) {
    for (const y of [107, 125]) { glassWin(p, i + 1, y, 3, 8, rounded, '#fff2d2'); addWin(r, i + 1, y, 3, 8, rounded, 0.4); }
    glassWin(p, i, 143, 5, 12, rounded, '#ecdcb8', '#0e0c16');                       // the ground floor's arcade
  }
  // the lion of St Mark on its red flag, from a pole over the corner (it stirs per frame)
  p.fill(x0 + 2, top - 18, 1, 18, '#6a5a52');
}

/* ================= The Fondaco dei Tedeschi at the east foot, its merlons and its altana ================= */
const FLAG = { x: CAMER.x0 + 3, y: CAMER.top - 18 };
function drawFondaco(p, r) {
  const { x0, x1, top } = FOND;
  WIN_LAYER = 1;
  for (let y = top; y < WL; y++) for (let i = x0; i < x1; i++) {
    const glow = Math.max(0, 1 - Math.abs(i - 350) / 70) * 0.3;
    let v = 0.34 + glow + (y - top) / (WL - top) * 0.12 + (vnoise(i / 4, y / 3, 13) - 0.5) * 0.12;
    if (i === x0 || i === x0 + 1 || i === x1 - 1 || i === x1 - 2) v += 0.18;        // corner quoins
    if (y === 112 || y === 124 || y === 136) v += 0.2;
    p.set(i, y, pick(OCHRE, v, i, y, 0.6));
  }
  for (let i = x0; i < x1; i += 4) for (let y = top - 4; y < top; y++) if (((i - x0) >> 1) % 2 === 0) { p.set(i, y, '#6a5a50'); p.set(i + 1, y, '#4a3e40'); }   // merlons
  for (let i = x0; i < x1; i++) p.set(i, top, '#8a7a6a');
  chimney(p, x0 + 6, top - 4, '#3a2e34', '#5a4a48'); chimney(p, x1 - 8, top - 4, '#3a2e34', '#5a4a48');
  for (let y of [101, 114, 126]) for (let i = x0 + 5; i < x1 - 4; i += 5) { glassWin(p, i, y, 2, 6, rounded, '#a89880'); addWin(r, i, y, 2, 6, rounded, 0.42); }
  for (let k = 0; k < 5; k++) glassWin(p, x0 + 7 + k * 12, 140, 7, 16, rounded, '#cbb89a', '#0c0a14');   // the water arcade
  // the altana: a wooden roof terrace on posts, its lattice railing
  const ax0 = 362, ax1 = 404, ay = 88;
  for (const px of [ax0, ax0 + 14, ax0 + 28, ax1]) p.fill(px, ay, 1, top - ay - 4, '#3a2c26');
  p.fill(ax0 - 1, ay, ax1 - ax0 + 3, 1, '#6a5240');
  for (let i = ax0; i <= ax1; i++) { p.set(i, ay - 4, '#5a4434'); if ((i & 1) === 0) { p.set(i, ay - 3, '#3a2c26'); p.set(i, ay - 1, '#3a2c26'); } else p.set(i, ay - 2, '#3a2c26'); }
  for (const px of [ax0 + 1, ax1 - 1]) p.fill(px, ay - 16, 1, 12, '#3a2c26');          // the poles of the washing line
}

/* ================= The near palazzi: two on the San Polo bank, one red Gothic one on the Riva del Carbon ================= */
const NEAR_HOUSES = [
  { x0: 30, x1: 68, top: 70, pal: ROSE, poly: 4, seed: 3 },
  { x0: 68, x1: 100, top: 82, pal: OCHRE, poly: 3, seed: 4 },
  { x0: 420, x1: 482, top: 58, pal: BRICK, poly: 5, seed: 5 },
];
const CHANDELIERS = [];                               // lit rooms where a Murano chandelier hangs in the polifora
function drawNearHouse(p, r, hs) {
  const { x0, x1, top, pal, poly } = hs, w = x1 - x0;
  WIN_LAYER = 2;
  for (let y = top; y < NWL; y++) for (let i = x0; i < x1; i++) {
    let v = 0.42 + (vnoise(i / 5, y / 4, hs.seed) - 0.5) * 0.22 + (y > NWL - 22 ? -0.12 : 0) + (i === x0 ? 0.2 : 0) - (i === x1 - 1 ? 0.12 : 0);
    if (y >= NWL - 4) v -= 0.25;                                                     // the green-black tide line
    p.set(i, y, y >= NWL - 4 ? pick(['#0e1a18', '#14241e', '#1c3026'], v + 0.4, i, y) : pick(pal, v, i, y, 0.7));
  }
  for (let i = x0 - 1; i <= x1; i++) { p.set(i, top, '#cfc0a8'); p.set(i, top + 1, '#6a5a58'); if ((i & 1) === 0) p.set(i, top + 2, '#8a7a70'); }   // dentil cornice
  chimney(p, x0 + 5, top, '#2a2026', '#4a3a3a'); chimney(p, x1 - 7, top, '#2a2026', '#4a3a3a');
  const cx = (x0 + x1) >> 1, rows = [top + 10, top + 34, top + 58].filter(y => y < NWL - 30);
  rows.forEach((fy, k) => {
    const nobile = k === 1;
    if (nobile) {                                                                   // the piano nobile's polifora and its balcony
      const lw = 3, gap = 1, tw = poly * lw + (poly - 1) * gap, sx = cx - (tw >> 1);
      for (let q = 0; q < poly; q++) { glassWin(p, sx + q * (lw + gap), fy, lw, 11, pointed, null); }
      for (let i = sx - 1; i <= sx + tw; i++) { p.set(i, fy - 1, '#e6d6b8'); p.set(i, fy + 11, '#e6d6b8'); }
      for (let q = 0; q <= poly; q++) p.fill(sx - 1 + q * (lw + gap), fy, 1, 11, '#e6d6b8');
      for (let q = 0; q < poly - 1; q++) { p.set(sx + q * (lw + gap) + 3, fy - 3, '#e6d6b8'); p.set(sx + q * (lw + gap) + 2, fy - 2, '#e6d6b8'); p.set(sx + q * (lw + gap) + 4, fy - 2, '#e6d6b8'); }   // quatrefoil hints
      p.fill(sx - 3, fy + 12, tw + 6, 1, '#d8c8aa'); for (let i = sx - 3; i < sx + tw + 3; i += 2) p.set(i, fy + 13, '#a89a86'); p.fill(sx - 3, fy + 14, tw + 6, 1, '#8a7c6c');
      WINS.push({ id: WINS.length, x: sx, y: fy, w: tw, h: 11, rows: null, poly, lw, gap, n: 2 + Math.floor(r() * 3), p: 0.78, off: r(), col: '#ffcf7a', layer: 2 });
      CHANDELIERS.push({ x: cx, y: fy + 2, win: WINS.length - 1 });
      for (const sx2 of [x0 + 4, x1 - 8]) if (sx2 + 4 < sx - 4 || sx2 > sx + tw + 4) { glassWin(p, sx2, fy, 3, 9, pointed, '#e6d6b8'); addWin(r, sx2, fy, 3, 9, pointed, 0.55); }
    } else {
      for (let i = x0 + 4; i + 3 < x1 - 2; i += 8) {
        glassWin(p, i, fy, 3, 8, pointed, '#c8b8a0');
        p.fill(i - 2, fy, 1, 8, SHUT); p.fill(i + 4, fy, 1, 8, SHUT_L);
        addWin(r, i, fy, 3, 8, pointed, 0.4);
      }
    }
  });
  // the water door: a pointed arch onto the canal, its steps green with weed
  const dx = x0 + (w >> 1) - 4;
  glassWin(p, dx, NWL - 16, 8, 16, pointed, '#b8aa96', '#080c10');
  p.fill(dx, NWL - 2, 8, 2, '#1c3a2c');
  if (hs.x0 > 400) { glassWin(p, x0 + 6, NWL - 30, 4, 9, pointed, '#c8b8a0'); addWin(r, x0 + 6, NWL - 30, 4, 9, pointed, 0.6); }
}
function renderNear() {
  const p = pix(W, NWL + 1), r = rng(41);
  for (const hs of NEAR_HOUSES) drawNearHouse(p, r, hs);
  return p.done();
}
/** the mooring poles (paline) before the palazzi: spiralling stripes, a gilt knob */
const PALINE = [[44, '#2a5aa8'], [50, '#2a5aa8'], [56, '#2a5aa8'], [430, '#b8323a'], [437, '#b8323a'], [462, '#2a7a5a'], [468, '#2a7a5a']];
function renderPaline() {
  const p = pix(W, 60), y0 = 132;                       // rows 132 .. 191 of the frame
  for (const [x, col] of PALINE) {
    const top = 148 - (x % 3) * 3, bot = 184;
    for (let y = top; y < bot; y++) {
      const s = ((y + x) % 6) < 3;
      p.set(x, y - y0, s ? col : '#e8e0d0'); p.set(x + 1, y - y0, s ? mixHex(col, '#000000', 0.4) : '#a8a0a0');
    }
    p.fill(x, top - 2 - y0, 2, 2, '#d4a24c'); p.set(x, top - 3 - y0, '#f0cc78');
  }
  return { c: p.done(), y0 };
}

/* ================= The Rialto Bridge: the shops' arcades and their stepped roofs, the portico (back) ================= */
const BLOCKS = [];                                    // the shop blocks: { x0, x1, floor, wall, roof }
for (let k = 0; k < 6; k++) for (const side of [-1, 1]) {
  const x0 = side < 0 ? 160 + k * 13 : 262 + (5 - k) * 13, x1 = x0 + 13, floor = Math.round(deckY(x0 + 6.5)) - 4;
  BLOCKS.push({ x0, x1, floor, wall: floor - 9, roof: floor - 13, side, k });
}
const BR_LANTERNS = [[PORT.x0 - 2, 110], [PORT.x1 + 1, 110], [CAMER.x1 + 1, 139], [FOND.x0 - 2, 139]];   // the bridge's own lanterns, the last two at its ends
function renderBridgeBack() {
  const p = pix(W, SKY_H), r = rng(66);
  WIN_LAYER = 4;
  // stepped roofs first (the higher block's end wall shows where they step), then the arcade walls
  for (const b of BLOCKS) {
    for (let y = b.roof; y < b.wall; y++) for (let i = b.x0; i < b.x1; i++) {
      let col = pick(LEAD, 0.25 + (y - b.roof) * 0.12 + (i % 3 === 0 ? 0.08 : 0), i, y, 0.6);
      if (y === b.roof) col = '#8a8a96';
      p.set(i, y, col);
    }
    for (let y = b.wall; y <= b.floor + 4; y++) for (let i = b.x0; i < b.x1; i++) {
      const lit = 0.55 + (y - b.wall) * 0.025 + (1 - Math.abs(i - BR.cx) / 100) * 0.15;
      let col = pick(ISTRIA, lit + (vnoise(i / 2, y / 2, 3) - 0.5) * 0.1, i, y, 0.5);
      if (y === b.wall) col = '#fff2d2'; else if (y === b.wall + 1) col = '#9c8c7a';
      p.set(i, y, col);
    }
    const ox = b.x0 + 4;                                                           // the arched shop opening
    glassWin(p, ox, b.wall + 3, 5, 7, rounded, null, (b.k + (b.side > 0 ? 1 : 0)) % 3 === 1 ? SHUT : '#1a1420');
    p.fill(b.x0, b.wall + 2, 1, 8, '#fff2d2');                                   // the pilaster between two shops
    addWin(r, ox, b.wall + 3, 5, 7, rounded, 0.5);
  }
  // the portico: two piers, a tall arch, the entablature and the pediment
  const { x0, x1, top, peak } = PORT, base = Math.round(deckY(BR.cx)) - 1;
  for (let y = top; y <= base; y++) for (let i = x0; i < x1; i++) {
    let col = pick(ISTRIA, 0.66 + (y - top) * 0.008 + (vnoise(i / 2, y / 2, 5) - 0.5) * 0.1, i, y, 0.5);
    if (i === x0 + 1 || i === x1 - 2) col = '#fff6dc';                            // pilasters
    if (y === top || y === top + 3) col = '#fff6dc'; else if (y === top + 1 || y === top + 2) col = '#a89a88';
    p.set(i, y, col);
  }
  for (let y = 101; y <= base; y++) {                                              // the arch, lit from inside
    const hw = y < 104 ? [2, 4, 5][y - 101] : 6;
    for (let i = -hw; i < hw; i++) p.set(BR.cx + i, y, y < 103 ? '#f6c878' : '#e8a858');
  }
  for (let i = -7; i < 7; i++) p.set(BR.cx + i, 100, i === -1 || i === 0 ? '#fff6dc' : '#d8c8a8');   // its archivolt and keystone
  p.set(BR.cx - 7, 101, '#d8c8a8'); p.set(BR.cx + 6, 101, '#d8c8a8');
  for (let y = peak; y < top; y++) {                                               // the pediment
    const hw = Math.round((y - peak) * 1.35) + 1;
    for (let i = -hw; i < hw; i++) {
      const edge = i === -hw || i === hw - 1 || y === top - 1;
      p.set(BR.cx + i, y, edge ? '#fff6dc' : pick(ISTRIA, 0.5 + (y - peak) * 0.02, BR.cx + i, y, 0.5));
    }
  }
  p.fill(BR.cx - 1, peak - 1, 2, 1, '#fff6dc');
  for (const [lx, ly] of BR_LANTERNS.slice(0, 2)) { p.fill(lx, ly, 1, 4, '#2a2228'); p.fill(lx - 1, ly - 3, 3, 3, '#2a2228'); p.set(lx, ly - 2, '#ffe8b0'); }
  return p.done();
}

/* ================= The Rialto Bridge: balustrade, cornice, spandrels, the arch and its abutments (front) ================= */
function renderBridgeFront() {
  const p = pix(W, WL + 1);
  for (let i = 146; i <= 354; i++) {
    const y0 = Math.round(deckY(i)), inArch = i > BR.x0 && i < BR.x1, ay = inArch ? Math.round(archY(i)) : WL + 1;
    const glow = 1 - Math.abs(i - BR.cx) / 140;
    // balustrade: handrail, balusters, plinth
    p.set(i, y0 - 4, '#fff2d2');
    p.set(i, y0 - 3, i % 2 ? '#e8d8b8' : '#4a3e48'); p.set(i, y0 - 2, i % 2 ? '#d2c0a0' : '#3a3240');
    p.set(i, y0 - 1, '#c8b694');
    p.set(i, y0, '#fff6dc'); p.set(i, y0 + 1, '#8c8078');                           // the cornice
    for (let y = y0 + 2; y < Math.min(ay - 4, WL + 1); y++) {                        // spandrel or abutment
      let v = 0.48 + glow * 0.18 + (y - y0) / 40 * 0.25 + (vnoise(i / 3, y / 2, 9) - 0.5) * 0.12;
      if (!inArch && (y - y0) % 5 === 4) v -= 0.18;                                  // rusticated courses
      if (!inArch && y > WL - 4) v = 0.1;                                            // weed at the waterline
      p.set(i, y, !inArch && y > WL - 4 ? pick(['#0e1a18', '#16261e', '#22362a'], 0.5, i, y) : pick(ISTRIA, v, i, y, 0.6));
    }
    if (inArch) {
      const ang = Math.atan2(i - BR.cx, ARCH_CY - ay);                                // voussoir joints
      const joint = Math.abs(frac(ang * 22 / Math.PI + 0.5) - 0.5) < 0.06;
      for (let k = 1; k <= 4; k++) {
        const y = ay - k;
        if (y > WL) continue;
        p.set(i, y, k === 4 ? '#fff6dc' : joint ? '#8c8078' : pick(ISTRIA, 0.78 + glow * 0.1 - k * 0.02, i, y, 0.5));
      }
      for (let k = 0; k < 4; k++) if (ay + k <= WL) p.set(i, ay + k, pick(['#2a2630', '#3a3440', '#4e4650', '#686064'], 0.95 - k * 0.3, i, ay + k));   // the vault's underside
    }
  }
  for (let i = -2; i <= 2; i++) for (let y = BR.crown - 6; y < BR.crown; y++) p.set(BR.cx + i, y, Math.abs(i) === 2 ? '#a89a88' : '#fff6dc');   // keystone
  for (const [rx, ry] of [[206, 133], [294, 133]]) {                                 // the Annunciation reliefs on the spandrels
    p.fill(rx, ry, 3, 6, '#f2e4c4'); p.fill(rx + 1, ry - 1, 1, 1, '#f2e4c4'); p.fill(rx - 1, ry + 6, 5, 1, '#a89a88');
  }
  return p.done();
}
/** the stone a shade warmer, laid over the bridge as its floodlights breathe (the slow motion on the landmark) */
function renderBridgeWarm(back, front) {
  const [c, x] = makeCanvas(W, WL + 1), p = pix(W, WL + 1);
  x.drawImage(back, 0, 0); x.drawImage(front, 0, 0);
  const d = x.getImageData(0, 0, W, WL + 1).data;
  for (let y = 84; y <= WL; y++) for (let i = 140; i < 360; i++) {
    const o = (y * W + i) * 4;
    if (!d[o + 3] || d[o] < 150) continue;                                         // only the lit stone
    if (bayer(i, y) < 0.6) p.set(i, y, '#ffb860', 140);
  }
  return p.done();
}

/* ================= The Grand Canal: dark green water, the mirror, ripples, streaks, rings ================= */
const WATER = ['#06100f', '#081614', '#0c1c19', '#10221e', '#152a25', '#1b332c', '#243c34', '#30463e'];
function renderWater() {
  const p = pix(W, RIVA - WL);
  for (let y = 0; y < RIVA - WL; y++) for (let i = 0; i < W; i++) {
    const far = 1 - y / (RIVA - WL), mid = Math.max(0, 1 - Math.abs(i - BR.cx) / 200);
    p.set(i, y, pick(WATER, 0.12 + far * 0.36 - mid * far * 0.06 + (vnoise(i / 12, y / 1.4, 21) - 0.5) * 0.12, i, y, 0.6));
  }
  return p.done();
}
const RIPPLES = [];
{ const r = rng(91); for (let y = WL + 2; y < RIVA - 1; y += 2) for (let j = 0; j < 3; j++) RIPPLES.push({ y: y + (j & 1), sp: (r() < 0.5 ? -1 : 1) * (1 + Math.floor(r() * 3)), ph: r(), len: 3 + Math.floor(r() * (4 + (y - WL) * 0.25)), glint: j === 2 }); }
const RINGS = [];
{ const r = rng(71); for (let i = 0; i < 40; i++) RINGS.push({ id: i, n: 90 + Math.floor(r() * 40), ph: r() }); }
/** the lights that lay a streak on the canal: [x, width, colour, alpha, the window whose light it is (or -1)] */
const STREAKS = [];
function drawRings(t) {
  for (const g of RINGS) {
    const { cyc, u } = cycle(t, g.n, g.ph);
    if (u > 0.45) continue;
    const s = u / 0.45, x = Math.floor(hash(g.id, cyc, 1) * W), y = Math.floor(WL + 3 + hash(g.id, cyc, 2) * (RIVA - WL - 5));
    const k = (y - WL) / (RIVA - WL), rr = 1 + s * (1.5 + k * 3.5), ry = rr * 0.32, pts = 6 + Math.floor(rr * 2.5);
    ctx.fillStyle = STREAKS.some(st => Math.abs(st[0] - x) < st[1] + 2) ? '#ffcf80' : '#7a9a90';
    ctx.globalAlpha = (1 - s) * 0.5;
    for (let j = 0; j < pts; j++) { const a = j / pts * TAU; ctx.fillRect(Math.round(x + Math.cos(a) * rr), Math.round(y + Math.sin(a) * ry), 1, 1); }
  }
  ctx.globalAlpha = 1;
}
/** the far lights lying on the canal through the arch, in a few broken dashes that shimmer */
function drawFarGlints(t) {
  const st = step(t, 480);
  for (const [x, wi, col] of FAR_GLINTS) {
    if (wi >= 0 && !winLit(WINS[wi], t)) continue;
    ctx.fillStyle = col;
    for (let k = 0; k < 2; k++) {
      const hv = hash(x, st, k), y = FAR_BASE + 1 + k * 4 + Math.floor(hash(x, st, k + 7) * 3);
      if (hv > 0.4 || y >= WL || !underArch(x, y)) continue;
      ctx.globalAlpha = 0.25 + hv;
      ctx.fillRect(x + Math.round((hash(x, st, k + 3) - 0.5) * 2), y, 1, 1);
    }
  }
  ctx.globalAlpha = 1;
}
function drawWaterFar(t) {                            // the water base and the mirror of the bridge and the far banks
  ctx.globalAlpha = 0.92; ctx.drawImage(FAR_WATER_C.c, FAR_WATER_C.x, FAR_WATER_C.y); ctx.globalAlpha = 1;   // under the arch
  drawFarGlints(t);
  ctx.drawImage(WATER_C, 0, WL);
  reflect(ctx, t, { top: WL, rows: RIVA - WL, squash: 1.35, alpha: 0.5, k1: 45, k2: 110, amp: 0.5, grow: 0.04 });
  ctx.drawImage(ARCH_VEIL_C.c, ARCH_VEIL_C.x, WL + ARCH_VEIL_C.y);
}
function drawWaterNear(t, rain) {                     // the near palazzi's mirror, then everything that lies on the water
  for (const [x, w] of [[26, 76], [418, 62]]) {
    ctx.drawImage(WATER_C, x, NWL - WL, w, RIVA - NWL, x, NWL, w, RIVA - NWL);
    reflect(ctx, t, { top: NWL, rows: RIVA - NWL, squash: 1.5, alpha: 0.46, x, w, k1: 45, k2: 110, amp: 0.5, grow: 0.05 });
  }
  for (const q of RIPPLES) {                                                    // troughs and glints sliding with the current
    const x = Math.round(frac(q.ph + q.sp * t / LOOP) * (W + 40)) - 20;
    ctx.globalAlpha = q.glint ? 0.16 : 0.24; ctx.fillStyle = q.glint ? (Math.abs(x - BR.cx) < 70 ? '#e8c890' : '#6a8a88') : '#030808';
    ctx.fillRect(x, q.y, q.len, 1);
  }
  ctx.globalAlpha = 1;
  for (let j = 0; j < 6; j++) {                                                 // long slow sheens of the mist lying on the water
    const y = WL + 6 + j * 8, len = 30 + (j * 23) % 40, x = Math.round(frac(j * 0.37 + (j & 1 ? 1 : -1) * t / LOOP) * (W + 120)) - 60;
    ctx.globalAlpha = 0.07 + 0.04 * wave(t, 5 + j, j * 0.3); ctx.fillStyle = '#8a9a9c';
    ctx.fillRect(x, y, len, 1); ctx.fillRect(x + 5, y + 1, len - 12, 1);
  }
  ctx.globalAlpha = 1;
  for (const [x, w, col, a, wi, y0, y1] of STREAKS) {
    if (wi >= 0 && !winLit(WINS[wi], t)) continue;
    streak(ctx, t, x, w, col, a, x * 0.37, y0, y1);
  }
  const lv = flick(t, LAMP_FLICK, 21);
  LAMPS.forEach((x, i) => streak(ctx, t, x + 1, 3, '#ffcf80', 0.32 * (i ? 1 : lv), 70 + i, RIVA - 12, RIVA));   // the quay lamps, close under us
  if (rain) drawRings(t);
}
/** the mooring poles and their wavering reflections */
function drawPaline(t) {
  ctx.drawImage(PALINE_C.c, 0, PALINE_C.y0);
  for (const [x, col] of PALINE) {
    for (let k = 0; k < 12; k++) {
      const off = Math.round(Math.sin(TAU * 45 * t / LOOP + k * 0.7 + x) * (0.5 + k * 0.1));
      ctx.globalAlpha = 0.34 * (1 - k / 12);
      R(x + off, 184 + k, 2, 1, ((k + x) % 6) < 3 ? col : '#a8a0a0');
    }
  }
  ctx.globalAlpha = 1;
}

/* ================= Drizzle: fine drops at three depths, rings on the canal, specks on the quay ================= */
const RAIN_LIGHTS = [
  ...LAMPS.map(x => ({ x, y: LAMP_Y, r: 34 })), { x: (AWN.x0 + AWN.x1) >> 1, y: AWN.y + 14, r: 46 },
  { x: BR.cx, y: 104, r: 30 }, ...BR_LANTERNS.slice(2).map(([x, y]) => ({ x, y, r: 14 })),
].map(l => ({ ...l, r2: l.r * l.r }));
const RAIN_LIGHTS_BB = [...RAIN_LIGHTS, { x: BILL.x + (BILL.w >> 1), y: BILL.y - 2, r: 26, r2: 26 * 26 }];
function litAt(x, y, lights) {
  for (const l of lights) { const dx = x - l.x, dy = y - l.y; if (dx * dx + dy * dy < l.r2) return true; }
  return false;
}
const DRIZZLE = [
  { count: 150, nMin: 110, nMax: 140, fall: 150, ff: 1, len: 1, slope: 0.1, col: 'rgba(140,150,170,0.32)', warm: 'rgba(255,205,140,0.5)', splash: null,
    land: (x, a) => 112 + Math.floor(a * 44) },
  { count: 100, nMin: 150, nMax: 180, fall: 210, ff: 1, len: 2, slope: 0.13, col: 'rgba(160,172,190,0.38)', warm: 'rgba(255,210,150,0.62)', splash: null,
    land: (x, a) => WL + 2 + Math.floor(a * (RIVA - WL - 3)) },
  { count: 46, nMin: 190, nMax: 220, fall: 280, ff: 0.86, len: 4, slope: 0.18, col: 'rgba(190,200,220,0.46)', warm: 'rgba(255,218,160,0.75)', splash: '#a8b4c4',
    land: (x, a) => RIVA + 8 + Math.floor(a * (H - RIVA - 10)) },
];
DRIZZLE.forEach((L, li) => {
  const r = rng(200 + li);
  L.drops = [];
  for (let i = 0; i < L.count; i++) L.drops.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r() });
});
function drawDrizzle(L, t, lights) {
  for (const d of L.drops) {
    const q = t * d.n / LOOP + d.ph, fq = Math.floor(q), cyc = fq % d.n, u = q - fq;
    const xl = -24 + hash(d.id, cyc, 11) * (W + 24 + L.fall * L.slope);
    const yl = L.land(Math.round(xl), hash(d.id, cyc, 12));
    if (u < L.ff) {
      const s = u / L.ff, yh = yl - L.fall * (1 - s), xh = xl - (yl - yh) * L.slope;
      if (yh < -1) continue;
      ctx.fillStyle = litAt(xh, yh, lights) ? L.warm : L.col;
      for (let k = 0; k < L.len; k++) ctx.fillRect(Math.round(xh - k * L.slope), Math.round(yh - k), 1, 1);
    } else if (L.splash) {
      const s = (u - L.ff) / (1 - L.ff), spread = 1 + s * 1.6;
      ctx.globalAlpha = (1 - s) * 0.8;
      ctx.fillStyle = litAt(xl, yl, lights) ? L.warm : L.splash;
      const Y = Math.round(yl - 1 - Math.sin(Math.PI * s) * 1.4);
      ctx.fillRect(Math.round(xl - spread), Y, 1, 1); ctx.fillRect(Math.round(xl + spread), Y, 1, 1);
      ctx.globalAlpha = 1;
    }
  }
}
// a picked weather (site/js/engine/weather.js): the drizzle is Venice's own; rain, snow and leaves fall behind the bridge,
// onto the canal and onto the quay, and catch the quay lamps, the awning, the bridge's light and the sign's lamps
const WEATHER = weatherPlans(['drizzle']);
const WX_LAND = [[112, WL], [WL + 2, RIVA - 1], [RIVA + 8, H - 2]];
const WX = { land: WX_LAND, lights: RAIN_LIGHTS.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r })) };
const WX_BB = { land: WX_LAND, lights: RAIN_LIGHTS_BB.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r })) };

/* ================= The quay: the Istrian stone edge, wet trachyte paving, the lamps' pools ================= */
const PAVE = ['#0c0e18', '#121624', '#1a1e2e', '#242838', '#30323e', '#403e46', '#58504c', '#786654', '#9a805c'];
const IRON = { d: '#0a0c12', m: '#161a24', h: '#343c4a' };
function lampLight(x, y) {                            // how much lamplight falls on (x, y), 0..1
  let L = 0;
  for (const lx of LAMPS) L = Math.max(L, Math.exp(-((x - lx) ** 2) / 1600 - ((y - 214) ** 2) / 1400));
  L = Math.max(L, 0.85 * Math.exp(-((x - 92) ** 2) / 2600 - ((y - 222) ** 2) / 700));                // under the awning's bulbs
  L = Math.max(L, 0.7 * Math.exp(-((x - (BAR.door + 7)) ** 2) / 300 - ((y - 240) ** 2) / 900));      // the bacaro's door
  return L;
}
const SLAB_ROWS = (() => { const a = []; let j = 4, h = 3; while (j < H - RIVA) { a.push(j); j += Math.round(h); h += 0.7; } return a; })();
const PUDDLES = [[148, 252, 20, 2.6], [252, 261, 28, 3], [352, 244, 18, 2.2], [426, 258, 22, 2.8], [92, 260, 14, 2]];   // x, y, rx, ry
const inPuddle = (i, y) => PUDDLES.find(([x, py, rx, ry]) => ((i - x) / rx) ** 2 + ((y - py) / ry) ** 2 < 1);
function renderQuay() {
  const p = pix(W, H - RIVA);
  for (let y = RIVA; y < H; y++) for (let i = 0; i < W; i++) {
    const j = y - RIVA, L = lampLight(i, y);
    let col;
    if (j < 3) col = pick(ISTRIA, 0.32 + L * 0.6 + (j === 0 ? 0.1 : 0) - j * 0.06, i, y, 0.5);    // the stone edge
    else if (j === 3) col = '#06080e';
    else {
      let row = 0; while (row + 1 < SLAB_ROWS.length && SLAB_ROWS[row + 1] <= j) row++;                    // slabs, larger nearer us
      const sw = 10 + row * 4, off = (row * 7) % sw, seam = (i + off) % sw === 0 || SLAB_ROWS[row] === j;
      const wet = (vnoise(i / 9, j / 3, 31) - 0.4) * 0.3;
      col = pick(PAVE, 0.22 + L * 0.62 + wet + (seam ? -0.14 : 0) + (hash(i >> 2, row, 7) - 0.5) * 0.06, i, y, 0.7);
    }
    const pd = j > 4 && inPuddle(i, y);
    if (pd) {                                                                      // a puddle: the night sky and the lamps lying in it
      const e = ((i - pd[0]) / pd[2]) ** 2 + ((y - pd[1]) / pd[3]) ** 2;
      col = e > 0.7 ? pick(PAVE, 0.5 + L * 0.4, i, y) : pick(['#0a0e1c', '#121a2c', '#1c2638', '#2a3446', '#5a5464', '#a08868'], 0.2 + L * 0.8 + (y < pd[1] ? 0.1 : 0), i, y, 0.6);
    }
    p.set(i, j, col);
  }
  return p.done();
}
/** what moves in the puddles: the lamps' light wavering, a sheen, rings when it drizzles */
function drawPuddles(t, rain) {
  PUDDLES.forEach(([x, y, rx, ry], k) => {
    for (const lx of [...LAMPS, 92]) {
      if (Math.abs(lx - x) > rx - 2) continue;
      for (let j = -Math.floor(ry) + 1; j < ry; j++) {
        const hw = rx * Math.sqrt(Math.max(0, 1 - (j / ry) ** 2)), off = Math.round(wave(t, 60 + k * 7, j * 0.3) * 1.2);
        if (Math.abs(lx + off - x) < hw - 1) { ctx.globalAlpha = 0.5 + 0.2 * wave(t, 120, k * 0.2 + j * 0.4); R(lx + off, y + j, 2, 1, '#ffd890'); }
      }
    }
    const sx = Math.round(x + (rx - 6) * wave(t, 3 + k, k * 0.31));
    ctx.globalAlpha = 0.22; R(sx - 3, y - 1, 6, 1, '#8a96b4'); ctx.globalAlpha = 1;
    if (!rain) return;
    for (let g = 0; g < 2; g++) {
      const { cyc, u } = cycle(t, 70 + k * 9 + g * 13, g * 0.5 + k * 0.17);
      if (u > 0.5) continue;
      const s = u / 0.5, gx = x + (hash(k, cyc, g) - 0.5) * (rx - 4) * 2, gy = y + (hash(k, cyc, g + 5) - 0.5) * (ry - 1), rr = 1 + s * 3;
      ctx.globalAlpha = (1 - s) * 0.6; ctx.fillStyle = '#9aa8c4';
      for (let a = 0; a < 10; a++) ctx.fillRect(Math.round(gx + Math.cos(a / 10 * TAU) * rr), Math.round(gy + Math.sin(a / 10 * TAU) * rr * 0.35), 1, 1);
    }
  });
  ctx.globalAlpha = 1;
}

/* ================= The bacaro: its corner, the lit door, the sign, the awning on its posts ================= */
const BAR_WALL = ['#16121a', '#201820', '#2c2026', '#3a2a2c', '#4a3632', '#5e4438'];
function renderBar() {
  const p = pix(AWN.x1 + 3, H), r = rng(29);
  WIN_LAYER = 3;
  for (let y = 0; y < H; y++) for (let i = 0; i < BAR.x1; i++) {
    const L = lampLight(i, Math.max(y, 200));
    p.set(i, y, pick(BAR_WALL, 0.25 + L * 0.6 * smooth(150, 230, y) + (vnoise(i / 4, y / 5, 17) - 0.5) * 0.2 + (i === BAR.x1 - 1 ? 0.18 : 0), i, y, 0.7));
  }
  for (let y = 0; y < H; y++) p.set(BAR.x1 - 1, y, y > 200 ? '#8a6a52' : '#4a3a38');                        // the corner's stone quoin
  for (const [wx, wy] of [[8, 60], [22, 60], [8, 120], [22, 120]]) {                                       // upper windows, green shutters
    glassWin(p, wx, wy, 5, 12, rounded, '#8a7a6a');
    p.fill(wx - 3, wy, 2, 12, SHUT); p.fill(wx + 6, wy, 2, 12, SHUT_L);
    addWin(r, wx, wy, 5, 12, rounded, 0.5);
  }
  for (let i = 5; i < 34; i++) p.set(i, 133, '#3a2a26');                                                   // a flower box, red geraniums
  for (let i = 6; i < 33; i += 2) { p.set(i, 132, '#2a4a2a'); if (hash(i, 3) < 0.6) p.set(i, 131, '#c8323a'); }
  for (let y = 40; y < H; y++) { p.set(BAR.x1 - 4, y, '#120e14'); if (y % 16 === 0) p.set(BAR.x1 - 4, y, '#4a3a38'); }   // a drainpipe
  // a capitello: the Madonna's little shrine on the wall, a candle under her
  for (let j = 0; j < 12; j++) for (let i = 0; i < 9; i++) { const edge = i === 0 || i === 8 || j === 0 || (j === 1 && (i === 1 || i === 7)); if (j === 0 && (i < 2 || i > 6)) continue; p.set(SHRINE.x - 4 + i, SHRINE.y + j, edge ? '#cfc0a8' : '#2a1e22'); }
  p.fill(SHRINE.x - 1, SHRINE.y + 3, 3, 6, '#3a5a9a'); p.fill(SHRINE.x, SHRINE.y + 2, 1, 2, '#e2c0a0'); p.set(SHRINE.x, SHRINE.y + 4, '#ece6da');
  p.fill(SHRINE.x - 5, SHRINE.y + 12, 11, 1, '#a89a86');
  p.fill(4, 168, 18, 7, '#ece6da'); p.fill(4, 168, 18, 1, '#ffffff'); p.text('RIVA', 5, 169, '#1a1a1a');     // the nizioleto, painted on the wall
  // the door: glazed, its frame, the warm room behind (bottles on a shelf), the sign board over it
  const { door, dw } = BAR;
  p.fill(door - 2, 198, dw + 4, 9, '#1a1216'); p.text('BACARO', door - 1, 200, '#e8b860');
  for (let y = 210; y < H; y++) for (let i = door; i < door + dw; i++) {
    const v = 0.9 - (y - 210) / 80 + (bayer(i, y) - 0.5) * 0.15;
    p.set(i, y, v > 0.7 ? '#ffcf7a' : v > 0.5 ? '#f2a85a' : '#c47a40');
  }
  for (let i = door; i < door + dw; i++) { p.set(i, 218, '#4a2a1a'); if (i % 2) { p.set(i, 216, ['#2a5a3a', '#8a1a2a', '#c89a3a'][i % 3]); p.set(i, 217, ['#2a5a3a', '#8a1a2a', '#c89a3a'][i % 3]); } }
  for (let y = 209; y < H; y++) { p.set(door - 1, y, '#2a1a14'); p.set(door + dw, y, '#2a1a14'); p.set(door + (dw >> 1), y, '#3a2418'); }
  p.fill(door - 1, 209, dw + 2, 1, '#2a1a14'); p.fill(door, 232, dw, 1, '#3a2418');
  // the awning: a striped valance with a scalloped hem, its posts
  const { x0, x1, y } = AWN;
  for (let j = 0; j < 12; j++) for (let i = x0; i < x1; i++) {
    const stripe = Math.floor((i - x0) / 4) & 1, hem = j >= 9;
    if (hem && ((i - x0) % 6 < 1 || (j === 11 && (i - x0) % 6 !== 3))) continue;            // scallops
    if (j < 3) { p.set(i, y + j - 3, pick(['#3a1218', '#4a1a20', '#5a2028'], 0.3 + j * 0.3, i, y + j)); }      // the canopy's slope
    const base = stripe ? ['#5a1620', '#7a2230', '#94303a'] : ['#8a7a5a', '#b0a074', '#ccbc8c'];
    p.set(i, y + j, pick(base, 0.9 - j * 0.07 + (i === x0 ? -0.4 : 0), i, y + j));
  }
  for (const px of [x0 + 2, x1 - 2]) { p.fill(px, y + 12, 1, TROW - y - 12, '#2a2026'); p.set(px, y + 12, '#6a5a4a'); p.fill(px - 1, TROW - 1, 3, 1, '#1a1216'); }
  return trim(p);
}
const SHRINE = { x: 19, y: 146 };
const AWN_BULBS = [];
for (let i = AWN.x0 + 5; i < AWN.x1 - 2; i += 9) AWN_BULBS.push([i, AWN.y + 13 + (((i - AWN.x0) / 9) & 1)]);
/** a bistro table (round marble top on one leg) */
function drawTable(x) {
  R(x - 5, TROW - 11, 11, 2, '#d8d0c0'); R(x - 5, TROW - 10, 11, 1, '#9a9286'); R(x, TROW - 9, 1, 8, '#2a2a30'); R(x - 2, TROW - 1, 5, 1, '#1a1a20');
}
/** a bistro chair, seen side-on (facing: 1 right, -1 left), its feet on row fy */
function drawChair(x, fy, facing, col = '#3a3438', hi = '#7a6a5a') {
  x = Math.round(x); fy = Math.round(fy);
  R(x - 2, fy - 7, 5, 1, hi);                                       // the seat
  R(x - 2, fy - 6, 1, 6, col); R(x + 2, fy - 6, 1, 6, col);         // legs
  const bx = facing > 0 ? x - 2 : x + 2;
  R(bx, fy - 13, 1, 6, col); R(bx, fy - 13, 1, 1, hi);             // the back
}
/** a stack of n chairs by the wall */
function drawStack(x, n) {
  for (let k = 0; k < n; k++) { const y = TROW - k * 2; R(x - 2, y - 7, 5, 1, k === n - 1 ? '#8a7a6a' : '#5a4e48'); R(x - 2, y - 6, 1, 2 + (k ? 0 : 4), '#3a3438'); R(x + 2, y - 6, 1, 2 + (k ? 0 : 4), '#3a3438'); R(x - 2, y - 13, 1, 6, '#3a3438'); }
}

/* ================= The quay's lamps ================= */
function renderLamps() {
  const p = pix(W, H);
  for (const bx of [232, 300, 470]) {                                             // stone bollards at the quay's edge
    p.fill(bx - 1, RIVA + 1, 4, 7, '#8c8078'); p.fill(bx - 1, RIVA + 1, 1, 7, '#c8b8a0'); p.fill(bx - 2, RIVA, 6, 2, '#b0a18c'); p.fill(bx - 1, RIVA + 8, 4, 1, '#1a1a22');
  }
  const sb = { x: 150, y: TROW };                                                 // the bacaro's chalkboard: OMBRA 2
  for (let j = 0; j < 14; j++) { p.set(sb.x + Math.round(j * 0.15), sb.y - 15 + j, '#4a3424'); p.set(sb.x + 22 - Math.round(j * 0.15), sb.y - 15 + j, '#4a3424'); }
  p.fill(sb.x + 1, sb.y - 15, 21, 11, '#1e2420'); p.fill(sb.x + 1, sb.y - 15, 21, 1, '#6a5038'); p.fill(sb.x + 1, sb.y - 5, 21, 1, '#6a5038');
  p.text('OMBRA', sb.x + 2, sb.y - 13, '#d8d8cc'); p.fill(sb.x + 9, sb.y - 7, 5, 1, '#e8b860');
  for (const x of LAMPS) {
    p.fill(x - 1, EDGE - 2, 4, 3, IRON.d); p.fill(x, LAMP_Y + 6, 2, EDGE - LAMP_Y - 8, IRON.m); p.fill(x + 1, LAMP_Y + 8, 1, EDGE - LAMP_Y - 10, IRON.h);
    p.fill(x - 1, LAMP_Y + 20, 4, 1, IRON.h);
    p.fill(x - 2, LAMP_Y - 6, 6, 1, IRON.d); p.fill(x - 1, LAMP_Y - 8, 4, 2, IRON.d); p.set(x, LAMP_Y - 9, IRON.d); p.set(x + 1, LAMP_Y - 9, IRON.d);
    for (let y = LAMP_Y - 5; y <= LAMP_Y + 3; y++) { p.set(x - 2, y, IRON.d); p.set(x + 3, y, IRON.d); }
    p.fill(x - 2, LAMP_Y + 4, 6, 1, IRON.d); p.fill(x - 1, LAMP_Y + 5, 4, 1, IRON.m);
    for (let y = LAMP_Y - 5; y <= LAMP_Y + 3; y++) for (let i = x - 1; i <= x + 2; i++) p.set(i, y, y < LAMP_Y - 2 ? '#fff4d0' : '#ffd890');
    p.fill(x, LAMP_Y - 5, 2, 9, '#fffae8');
  }
  return p.done();
}

/* ================= The stazio: moored gondolas under their covers, rocking at the quay ================= */
/** a gondola's hull, len px long, bow towards dir, its waterline on row wy, gunwale top row wy - 3 */
function drawHull(x, wy, len, dir, cover = null, lit = '#3a3a4c') {
  x = Math.round(x); wy = Math.round(wy);
  const bow = dir > 0 ? x + len - 1 : x, stern = dir > 0 ? x : x + len - 1;
  for (let k = 0; k < len; k++) {
    const i = dir > 0 ? x + k : x + len - 1 - k, e = k / (len - 1);                // e: 0 at the stern, 1 at the bow
    const rise = Math.round(Math.max(0, (0.12 - e) / 0.12) * 4 + Math.max(0, (e - 0.85) / 0.15) * 4);
    const top = wy - 3 - rise, bot = wy - (e < 0.05 || e > 0.95 ? 1 : 0);
    R(i, top, 1, bot - top + 1, '#07070c'); R(i, top, 1, 1, lit);
    if (cover && e > 0.18 && e < 0.84) { R(i, top - 2, 1, 2, cover); if (k % 5 === 0) R(i, top - 2, 1, 1, '#1a2a4a'); }
  }
  for (let j = 0; j < 6; j++) { R(bow, wy - 8 - j, 1, 1, '#c8ccd8'); if (j % 2 === 0 && j < 6) R(bow - dir, wy - 8 - j, 1, 1, '#9aa0b0'); }   // the ferro and its teeth
  R(bow + dir, wy - 13, 1, 1, '#c8ccd8');
  R(stern, wy - 8, 1, 2, '#2a2a36');                                                 // the stern's curl
}
/** draw something once into an offscreen canvas of w x h (build time): the drawing functions paint on the global ctx */
function offscreen(w, h, f) {
  const [c, x] = makeCanvas(w, h), keep = ctx;
  ctx = x; f(); ctx = keep;
  return c;
}
const MOORED = [[0, 386, 200, 50], [1, 404, 205, 54]];
let MOORED_C, POLES_C, HULL_C;
function renderStazio() {
  MOORED_C = MOORED.map(([k, x, wy, len]) => offscreen(len + 4, 20, () => {
    ctx.globalAlpha = 0.3; R(6, 17, len - 8, 1, '#000'); ctx.globalAlpha = 1;
    drawHull(2, 16, len, -1, k ? '#1c3a6a' : '#284a7a', '#4a4a5c');
  }));
  POLES_C = offscreen(W, RIVA + 1, () => {
    for (const [px, top] of [[382, 176], [446, 180], [474, 178]]) {               // the stazio's own striped poles
      for (let y = top; y < RIVA; y++) { const s = ((y + px) % 6) < 3; R(px, y, 2, 1, s ? '#2a4a8a' : '#e8e0d0'); }
      R(px, top - 2, 2, 2, '#d4a24c');
    }
  });
  HULL_C = [-1, 1].map(dir => offscreen(52, 20, () => drawHull(2, 16, 48, dir)));
}
function drawStazio(t) {
  MOORED.forEach(([k, x, wy], i) => {
    const bob = Math.round(0.6 * wave(t, 40 + k * 7, k * 0.3) + 0.4 * wave(t, 97, k * 0.5));
    ctx.drawImage(MOORED_C[i], x - 2, wy + bob - 16);
  });
  ctx.drawImage(POLES_C, 0, 0);
}

/* ================= The sign on the altana: VELA SPRITZ, a made-up aperitivo (the city's billboard) ================= */
const BIG = {
  V: ['#...#', '#...#', '#...#', '#...#', '.#.#.', '.#.#.', '..#..'], E: ['#####', '#....', '#....', '####.', '#....', '#....', '#####'],
  L: ['#....', '#....', '#....', '#....', '#....', '#....', '#####'], A: ['.###.', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
};
function renderAd(dim) {                               // dim: the right-hand lamp is out
  const { w, h } = BILL, p = pix(w, h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    let cone = 0;
    BILL_LAMPS.forEach((lx, k) => { const dx = (i - (lx - BILL.x)) / (6 + j * 0.9); cone = Math.max(cone, (k === 1 && dim ? 0.25 : 1) * Math.exp(-dx * dx) * Math.max(0, 1 - j / 16)); });
    p.set(i, j, pick(['#08202c', '#0e2c3a', '#143a48', '#1c4a58', '#2a5e6a'], 0.25 + 0.55 * cone, i, j, 0.5));
  }
  for (let i = 0; i < w; i++) { p.set(i, 0, '#d4a24c'); p.set(i, h - 1, '#8a6a30'); }
  for (let j = 0; j < h; j++) { p.set(0, j, '#d4a24c'); p.set(w - 1, j, '#8a6a30'); }
  // the glass: a stemmed goblet of orange spritz, a slice of orange on the rim
  for (let j = 4; j <= 10; j++) { const hw = j < 9 ? 3 : j === 9 ? 2 : 1; for (let i = -hw; i <= hw; i++) p.set(7 + i, j, j < 5 ? '#ffe2b0' : (i + j) & 1 ? '#f07a28' : '#f8922e'); }
  for (let j = 4; j <= 9; j++) p.set(4, j, '#ffd0a0');
  p.fill(7, 11, 1, 4, '#c8d8e0'); p.fill(5, 15, 5, 1, '#c8d8e0');
  p.set(10, 3, '#ffb040'); p.set(11, 3, '#ffb040'); p.set(10, 4, '#ffd070'); p.set(11, 2, '#ffd070');
  p.text('SPRITZ', 17, 3, '#ecdcb8');
  let cx = 15;
  for (const ch of 'VELA') {
    for (const [dx, dy, col] of [[1, 1, '#04121a'], [0, 0, '#f0c060']]) BIG[ch].forEach((row, j) => { for (let i = 0; i < 5; i++) if (row[i] === '#') p.set(cx + i + dx, 10 + j + dy, col); });
    cx += 7;
  }
  for (let i = 17; i < 40; i += 2) p.set(i, 8, '#d4a24c');
  return p.done();
}
function renderSign() {                                // the sign's frame, legs and lamp arms (only with the billboard)
  const p = pix(W, 100), { x, y, w, h } = BILL;
  p.fill(x - 1, y - 1, w + 2, h + 2, '#1a1418');
  for (const lx of [x + 6, x + w - 7]) p.fill(lx, y + h + 1, 2, 88 - y - h - 1, '#3a2c26');
  p.fill(x + 2, y + h + 3, w - 4, 1, '#2a201c');
  for (const lx of BILL_LAMPS) { p.fill(lx, BILL_LAMP_Y + 1, 1, 4, '#1a1418'); p.fill(lx - 2, BILL_LAMP_Y, 5, 1, '#2a2226'); p.set(lx - 1, BILL_LAMP_Y + 1, '#fff0c8'); p.set(lx, BILL_LAMP_Y + 1, '#fff0c8'); p.set(lx + 1, BILL_LAMP_Y + 1, '#fff0c8'); }
  return p.done();
}

/* ================= People: one function draws them all (after rome.js), lit by the lamps or by the night ================= */
const SKINS = ['#f2c8a0', '#e2ac82', '#c48c60', '#8c5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d0a858', '#96421c', '#d4d0cc'];
const TOPS = ['#c8404a', '#e8c040', '#ece6da', '#3a6ac8', '#2a9a8a', '#d87aa8', '#e8823a', '#4a8a4a', '#8a5ac8', '#c8d0e0', '#8a2a32', '#58b0d8'];
const BOTS = ['#33466e', '#22202c', '#b09a70', '#d8d4cc', '#3a3a58', '#4a3a30'];
const NIGHT = '#0c1222', WARM_RIM = '#ffd9a0', COOL_RIM = '#8ea4c6';
/** a look: proportions for a height of h px, colours dimmed for the night (dk) with a rim of lamplight (warm 0..1) or mist */
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.38, rim = mixHex(COOL_RIM, WARM_RIM, o.warm ?? 0), m = c => mixHex(c, NIGHT, dk);
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.36);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 22 ? 7 : h >= 19 ? 6 : h >= 14 ? 5 : 4, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(m(HAIRS[hair]), rim, 0.45), top: m(top), topD: mixHex(top, NIGHT, Math.min(0.9, dk + 0.28)),
    topL: mixHex(m(top), rim, 0.5), bot: m(bot), shoe: m('#221a20'), long: o.long || 0, skirt: o.skirt ? m(o.skirt) : null,
    hat: o.hat ? m(o.hat) : null, bag: o.bag ? m(o.bag) : null, apron: o.apron ? m(o.apron) : null, stripes: o.stripes ? m(o.stripes) : null,
    band: o.band ? m(o.band) : null };
}
/** the look of someone standing at (x, y): the nearer a lamp, the less night and the warmer the rim */
const lookAt = (x, y, h, skin, hair, top, bot, o = {}) => { const L = lampLight(x, y); return look(h, skin, hair, top, bot, { dk: 0.52 - L * 0.34, warm: Math.min(1, L * 1.5), ...o }); };
/**
 * One person, feet on row fy, centred on column x. Returns the row of the top of the head.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir: 1 right, -1 left)
 * o.step: stride frame (profile); o.sit: seated at fy; o.lean: head px towards a neighbour;
 * o.aL / o.aR: 0 down, 1 straight up, 2 bent up with a phone, 3 out, 4 bent up, 5 hand to the face, 6 hand to the mouth
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1), legC = P.skirt ? P.skin : P.bot;
  if (legs) {
    const ly = fy - legs + 1;
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, legC);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, legC); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, legC); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, legC); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
      if (P.skirt) R(x - 2, ly, 4, (legs >> 1) + 1, P.skirt);
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, legC); R(tx + bw - lw, ly + 2, lw, legs - 2, legC);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
      if (P.skirt) R(tx, ly, bw, (legs >> 1) + 1, P.skirt);
    }
  } else if (side) {                                   // seated in profile: thighs forward, shins down
    R(dir > 0 ? x - 1 : x - 3, fy - 1, 5, 2, P.bot); R(dir > 0 ? x + 2 : x - 3, fy + 1, 2, P.ll - 2, legC); R(dir > 0 ? x + 2 : x - 4, fy + P.ll - 1, 3, 1, P.shoe);
  } else if (view === 1) {
    R(tx, fy - 1, bw, 2, P.bot); R(tx, fy + 1, 2, 5, legC); R(tx + bw - 2, fy + 1, 2, 5, legC); R(tx, fy + 6, 2, 1, P.shoe); R(tx + bw - 2, fy + 6, 2, 1, P.shoe);
  } else R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty, bw, th, P.top);
  if (P.stripes) for (let j = 1; j < th; j += 2) R(tx, ty + j, bw, 1, P.stripes);   // the gondolier's striped shirt
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  if (view !== 1) R(tx, ty, bw, 1, P.topL);
  if (P.bag) { if (view === 0) R(tx + 1, ty + 1, bw - 2, th - 2, P.bag); else if (side) R(dir > 0 ? tx - 1 : tx + bw - 1, ty + 1, 2, th - 2, P.bag); }
  if (P.apron && view !== 0) R(side ? (dir > 0 ? tx + 1 : tx) : tx + 1, ty + 2, side ? bw - 1 : bw - 2, th - 2 + (legs >> 1), P.apron);
  if (side) {
    const st = o.aR || 0;
    if (st === 0) { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 1) { R(x + dir, hy - 1, 1, ty - hy + 2, P.topD); R(x + dir, hy - 3, 1, 2, P.skin); }
    else if (st === 2) { for (let k = 0; k < 4; k++) R(x + dir * (1 + k), ty + 1 - k, 1, 1, k < 3 ? P.topD : P.skin); R(x + dir * 5 - (dir < 0 ? 1 : 0), ty - 5, 2, 3, '#0e1018'); }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else if (st === 6) { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, hy + hh - 1, 1, 1, P.skin); }
    else { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty, 1, 1, P.skin); }
  } else {
    const arm = (ax, st, sg) => {
      if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
      else if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 3, 1, 2, P.skin); }
      else if (st === 2 || st === 4) {
        R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin);
        if (st === 2) { const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 2, 2, 3, '#0e1018'); if (view === 0) { R(p0, hy - 2, 2, 1, '#ffe2a8'); R(p0, hy - 1, 2, 1, '#8ab0e8'); } }
      } else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 1, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 1, 1, 1, P.skin); }
      else { R(ax, ty, 1, 2, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
    };
    arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1);
  }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) {
    R(hx - 1, hy, hw + 2, 1, P.hat); R(hx, hy - 2, hw, 2, P.hat);
    if (P.band) R(hx, hy - 1, hw, 1, P.band);
  }
  return hy;
}
/** an umbrella held over someone whose head top is at hy: canopy w px wide, wet sheen on its crown */
function umbrella(x, hy, w, col, hand = 0) {
  x = Math.round(x); const y = hy - 4, hl = w >> 1;
  R(x - hl + 2, y - 2, w - 4, 1, mixHex(col, '#c8d0e0', 0.35));
  R(x - hl + 1, y - 1, w - 2, 1, col); R(x - hl, y, w, 1, col);
  R(x - hl, y + 1, w, 1, mixHex(col, '#000000', 0.4));
  for (let k = -hl; k <= hl; k += 3) R(x + k, y + 1, 1, 1, mixHex(col, '#000000', 0.6));
  R(x, y - 3, 1, 1, '#2a2a30'); R(x + hand, y + 2, 1, 4, '#2a2a30');
}

/* ----- the cast: who is where, what they do, and their crowd rank (0 stays while anybody does; pairs share one) ----- */
const SEAT = TROW - 7;                                 // the row people sit on
const A_CHAIRS = [{ x: TABLES[0].x - 8, f: 1 }, { x: TABLES[0].x + 8, f: -1 }];
const B_CHAIR = { x: TABLES[1].x - 8, f: 1 }, B_SPARE = { x: TABLES[1].x + 8, f: -1 };
const P_WAITER = lookAt(30, TROW, 23, 1, 0, '#ece6da', BOTS[1], { apron: '#1a1a22', dk: 0.2, warm: 0.9 });
const P_HIM = lookAt(58, TROW, 23, 0, 1, TOPS[3], BOTS[2]), P_HER = lookAt(74, TROW, 22, 1, 4, TOPS[10], BOTS[1], { long: 3 });
const P_READER = lookAt(108, TROW, 23, 0, 5, '#6a6a5a', BOTS[0], { hat: '#3a3430' });
// the waiter's evening: [from, to, x from, x to, what he does, what he carries]
const WAITER = [
  [0, 34, 26, 26, 'polish'], [34, 40, 26, 58, 'walk'], [40, 46, 58, STACK_X + 3, 'walk', 'A1'], [46, 48, STACK_X + 3, STACK_X + 3, 'stack'],
  [48, 53, STACK_X + 3, 74, 'walk'], [53, 60, 74, STACK_X + 3, 'walk', 'A2'], [60, 62, STACK_X + 3, STACK_X + 3, 'stack'],
  [62, 65, STACK_X + 3, 66, 'walk'], [65, 74, 66, 66, 'wipe'], [74, 84, 66, 26, 'walk', 'glasses'], [84, 92, 26, 108, 'walk'],
  [92, 100, 108, STACK_X + 3, 'walk', 'B1'], [100, 102, STACK_X + 3, STACK_X + 3, 'stack'], [102, 106, STACK_X + 3, 116, 'walk'],
  [106, 112, 116, 116, 'wipe'], [112, 150, 116, 80, 'sweep', 'broom'], [150, 158, 80, 26, 'walk', 'broom'],
  [158, 160, 26, STACK_X + 3, 'walk'], [160, 166, STACK_X + 3, 58, 'walk', 'A1'], [166, 168, 58, 58, 'place'],
  [168, 170, 58, STACK_X + 3, 'walk'], [170, 176, STACK_X + 3, 74, 'walk', 'A2'], [176, 178, 74, 74, 'place'],
  [178, 184, 74, 26, 'walk'], [184, 186, 26, 26, 'polish'], [186, 192, 26, 66, 'walk', 'tray'], [192, 198, 66, 66, 'serve', 'tray'],
  [198, 204, 66, 26, 'walk'], [204, 208, 26, 26, 'polish'], [208, 210, 26, STACK_X + 3, 'walk'], [210, 217, STACK_X + 3, 108, 'walk', 'B1'],
  [217, 219, 108, 108, 'place'], [219, 226, 108, 26, 'walk'], [226, 240, 26, 26, 'polish'],
];
function waiterAt(t) {
  for (const [a, b, x0, x1, act, carry] of WAITER) if (t >= a && t < b) {
    const u = (t - a) / (b - a);
    if (act === 'sweep') {                                                 // short strokes, working slowly along the awning
      const x = lerp(x0, x1, u) + 10 * Math.sin(TAU * 7 * u);
      return { x, act, carry, dir: Math.cos(TAU * 7 * u) > 0 ? 1 : -1, u };
    }
    return { x: lerp(x0, x1, act === 'walk' ? smooth(0, 1, u) * 0.3 + u * 0.7 : u), act, carry, dir: x1 > x0 ? 1 : x1 < x0 ? -1 : act === 'polish' ? 1 : -1, u };
  }
  return { x: 26, act: 'polish', dir: 1, u: 0 };
}
/** where the chairs are at t: A1 and A2 at table A, B1 at table B, or on the stack (or in the waiter's hands) */
function chairsAt(t) {
  const a = !(t >= 40 && t < 166), a2 = !(t >= 53 && t < 176), b = !(t >= 92 && t < 217);
  const carried = (t >= 40 && t < 46) || (t >= 160 && t < 166) ? 'A1' : (t >= 53 && t < 60) || (t >= 170 && t < 176) ? 'A2' : (t >= 92 && t < 100) || (t >= 210 && t < 217) ? 'B1' : null;
  const stack = 3 + (a ? 0 : 1) + (a2 ? 0 : 1) + (b ? 0 : 1) - (carried ? 1 : 0);
  return { a, a2, b, carried, stack };
}
/** the couple: seated at table A, walking off to the left, coming back */
function coupleAt(t) {
  const { leave, gone, arrive, at, sit } = COUPLE;
  if (t < leave + 2 || t >= sit) return { sit: true };
  if (t < gone) { const u = (t - leave - 2) / (gone - leave - 2); return { x: lerp(TABLES[0].x - 2, -14, u), dir: -1 }; }
  if (t < arrive) return null;
  if (t < at) { const u = (t - arrive) / (at - arrive); return { x: lerp(-14, TABLES[0].x - 2, u), dir: 1 }; }
  return { x: TABLES[0].x - 2, dir: 1, wait: true };
}
function readerAt(t) {
  const { leave, gone, arrive, at, sit } = READER;
  if (t < leave + 1 || t >= sit) return { sit: true };
  if (t < gone) { const u = (t - leave - 1) / (gone - leave - 1); return { x: lerp(B_CHAIR.x, -12, u), dir: -1 }; }
  if (t < arrive) return null;
  if (t < at) { const u = (t - arrive) / (at - arrive); return { x: lerp(-12, B_CHAIR.x - 6, u), dir: 1 }; }
  return { x: B_CHAIR.x - 6, dir: 1, wait: true };
}
const CAST = [
  { x: 292, y: EDGE, rank: 0.2, act: 'watch', P: lookAt(292, EDGE, 21, 1, 3, '#3a4a5a', BOTS[0], { long: 3 }), umb: '#2a5a4a' },
  { x: 432, y: EDGE + 2, rank: 0.15, act: 'gondolier', P: lookAt(432, EDGE, 21, 2, 0, '#ece6da', BOTS[1], { stripes: '#1a2a5a', hat: '#d8c088', band: '#8a1a22' }) },
  { x: 236, y: WALK + 2, rank: 0.62, act: 'smoke', P: lookAt(236, WALK, 24, 1, 2, '#5a4a3a', BOTS[4], { hat: '#2a2a30' }), umb: null },
];
const WALKERS = [
  ...UMBRELLA.map(([t0, t1, dir]) => ({ t0, t1, dir, y: WALK, rank: 0.42, P: look(24, 0, 1, '#8a2a32', BOTS[1], { dk: 0.4, warm: 0.4, long: 2 }), umb: '#1a1a24' })),
  { t0: FRIENDS[0], t1: FRIENDS[1], dir: 1, y: NEAR - 6, rank: 0.55, pair: true, P: look(25, 2, 0, TOPS[6], BOTS[0], { dk: 0.42, warm: 0.4 }),
    P2: look(24, 0, 3, TOPS[9], BOTS[3], { dk: 0.42, warm: 0.4, long: 3 }), umb: '#c8b040' },
];
const walkF = t => Math.floor(t * 3.2) & 1;            // stride frame (3.2 a second: a whole number per loop)
let DL = [];                                           // the frame's people and things, drawn back to front
const put = (y, f) => DL.push({ y, f });

/* ----- the quay's people and the bacaro's furniture, sorted by the row of their feet ----- */
function drawCast(t, crowd) {
  DL = [];
  const ch = chairsAt(crowd > 0 ? t : 0), wa = waiterAt(t);                   // with nobody about, the chairs stay put
  // the furniture: the stack by the wall, the chairs at their tables, the tables
  put(TROW - 0.3, () => drawStack(STACK_X, ch.stack));
  if (ch.a) put(TROW - 0.2, () => drawChair(A_CHAIRS[0].x, TROW, A_CHAIRS[0].f));
  if (ch.a2) put(TROW - 0.2, () => drawChair(A_CHAIRS[1].x, TROW, A_CHAIRS[1].f));
  if (ch.b) put(TROW - 0.2, () => drawChair(B_CHAIR.x, TROW, B_CHAIR.f));
  put(TROW - 0.2, () => drawChair(B_SPARE.x, TROW, B_SPARE.f));
  for (const tb of TABLES) put(TROW, () => drawTable(tb.x));
  const glassesA = (t < 74 && t >= 0) || t >= 195, ombra = !(t >= 104 && t < 218);
  put(TROW + 0.1, () => {
    if (glassesA && crowd > 0) for (const gx of [TABLES[0].x - 3, TABLES[0].x + 2]) { R(gx, TROW - 15, 2, 3, '#f07a28'); R(gx, TROW - 15, 2, 1, '#ffb050'); R(gx, TROW - 12, 1, 1, '#c8d8e0'); }
    if (ombra && crowd > 0) { R(TABLES[1].x - 2, TROW - 14, 2, 2, '#8a1a2a'); R(TABLES[1].x - 2, TROW - 12, 1, 1, '#c8d8e0'); }
  });
  if (crowd <= 0) { flushDL(); return; }
  // the waiter (rank 0): polishing a glass at the door, carrying chairs, wiping, sweeping, serving
  put(TROW + 3, () => {
    const o = { view: wa.act === 'polish' ? 1 : 2, dir: wa.dir }, x = wa.x, y = TROW + 3;
    if (wa.act === 'walk' || wa.act === 'sweep') o.step = walkF(t);
    if (wa.act === 'polish') { const s = step(t, 480) & 1; o.aL = 4; o.aR = s ? 4 : 0; }
    if (wa.act === 'wipe') o.aR = 3;
    if (wa.carry === 'tray' || wa.carry === 'glasses') o.aR = 3;
    const hy = person(x, y, P_WAITER, o), ty = hy + P_WAITER.hh;
    if (wa.act === 'polish') { R(Math.round(x) - 1, ty + 1, 2, 3, '#c8d8e0'); R(Math.round(x) - 1, ty + 1, 2, 1, '#ffffff'); }
    if (wa.act === 'wipe') { const s = Math.round(2 * wave(t, 240)); R(Math.round(x) + wa.dir * 5 + s, TROW - 12, 3, 1, '#f2f0e8'); }
    if (wa.carry === 'tray' || wa.carry === 'glasses') {
      const tx = Math.round(x) + wa.dir * 4;
      R(tx - 2, ty + 1, 6, 1, '#b8b4ac');
      if (wa.carry === 'tray') for (const g of [0, 2]) { R(tx - 1 + g, ty - 2, 1, 3, '#f07a28'); R(tx - 1 + g, ty - 2, 1, 1, '#ffb050'); }
      else { R(tx - 1, ty - 1, 1, 2, '#c8d8e0'); R(tx + 1, ty - 1, 1, 2, '#c8d8e0'); }
    }
    if (['A1', 'A2', 'B1'].includes(wa.carry)) drawChair(Math.round(x) + wa.dir * 4, ty + 9, -wa.dir, '#3a3438', '#9a8a7a');
    if (wa.act === 'stack' || wa.act === 'place') { const k = wa.u < 0.5 ? 1 : 0; R(Math.round(x) + wa.dir * 3, ty + 3 + k * 2, 2, 1, P_WAITER.skin); }
    if (wa.carry === 'broom') {
      const bx = Math.round(x) + wa.dir * 2, fx = bx + wa.dir * (wa.act === 'sweep' ? 5 + Math.round(2 * Math.sin(TAU * 7 * wa.u * 4)) : 1);
      for (let k = 0; k < 16; k++) R(Math.round(lerp(bx, fx, k / 15)), ty + 1 + k, 1, 1, '#8a6a42');
      R(fx - 2, y - 1, 5, 2, '#5a4a2a');
      if (wa.act === 'sweep' && frac(t * 1.4) < 0.4) { ctx.globalAlpha = 0.5; R(fx + wa.dir * 3, y - 1, 1, 1, '#9a8a6a'); ctx.globalAlpha = 1; }
    }
  });
  // the late couple (rank 0): at table A, sipping, leaning together; walking off, coming back
  const cp = coupleAt(t);
  if (cp) {
    if (cp.sit) {
      const sip = frac(t / 14) < 0.18, sip2 = frac(t / 17 + 0.5) < 0.16, lean = frac(t / 40 + 0.2) < 0.4;
      put(TROW - 0.1, () => {
        person(A_CHAIRS[0].x + 1, SEAT, P_HIM, { view: 2, dir: 1, sit: true, aR: sip ? 6 : 3, lean: lean ? 1 : 0 });
        person(A_CHAIRS[1].x - 1, SEAT, P_HER, { view: 2, dir: -1, sit: true, aR: sip2 ? 6 : 3, lean: lean ? -1 : 0 });
      });
    } else {
      const f = cp.wait ? 0 : walkF(t);
      put(TROW + 1, () => {
        person(cp.x - 5 * cp.dir, TROW + 1, P_HIM, { view: cp.wait ? 1 : 2, dir: cp.dir, step: f });
        person(cp.x + 2 * cp.dir, TROW + 2, P_HER, { view: 2, dir: cp.wait ? -1 : cp.dir, step: f ^ 1, lean: cp.wait ? -1 : 0 });
      });
    }
  }
  // the reader (rank 0.3): his newspaper, a page turned now and then
  const rd = readerAt(t);
  if (rd && here(0.3, crowd)) {
    if (rd.sit) put(TROW - 0.1, () => {
      const hy = person(B_CHAIR.x + 1, SEAT, P_READER, { view: 2, dir: 1, sit: true, aR: 3 }), turn = frac(t / 20) < 0.06;
      const nx = B_CHAIR.x + 4;
      R(nx, hy + 2, turn ? 3 : 5, 7, '#d8d0bc'); R(nx, hy + 2, turn ? 3 : 5, 1, '#f0e8d4');
      for (let k = 0; k < 3; k++) R(nx + 1, hy + 4 + k * 2, turn ? 1 : 3, 1, '#8a8478');
    });
    else put(TROW + 2, () => person(rd.x, TROW + 2, P_READER, { view: rd.wait ? 1 : 2, dir: rd.dir, step: rd.wait ? 0 : walkF(t) }));
  }
  // those who stay: someone under an umbrella watching the bridge, the gondolier at the stazio, a smoker by the lamp
  for (const c of CAST) {
    if (!here(c.rank, crowd)) continue;
    put(c.y, () => {
      const o = { view: 0 };
      if (c.act === 'watch') { const u = frac(t / 36 + 0.2); if (u < 0.3) o.aR = 2; else if (u > 0.6 && u < 0.72) o.lean = 1; o.aL = 4; }
      if (c.act === 'gondolier') {
        const s = ((t % CALL_EVERY) + CALL_EVERY) % CALL_EVERY;
        o.view = s < 4 ? 2 : 1; o.dir = -1; if (s < 4) o.aR = 3; else if (s > 15 && s < 22) o.lean = -1;
      }
      if (c.act === 'smoke') { o.view = 0; if (frac(t / 12) < 0.2) o.aR = 5; }
      const hy = person(c.x, c.y, c.P, o);
      if (c.umb) umbrella(c.x - 1, hy, 13, c.umb, -1);
      if (c.act === 'watch' && o.aR === 2) halo(HL.phone, c.x + 4, hy - 1, 0.6);
      if (c.act === 'smoke') {
        const lit = frac(t / 12) < 0.2;
        R(c.x + 4, hy + 4 - (lit ? 2 : 0), 1, 1, lit ? '#ff8a40' : '#c85a30');
        for (let k = 0; k < 4; k++) {
          const { u } = cycle(t, 60, k / 4);
          ctx.globalAlpha = 0.4 * Math.sin(Math.PI * u); R(Math.round(c.x + 4 + u * 4 + wave(t, 37, k * 0.25) * 1.5), Math.round(hy + 3 - u * 16), 1, u > 0.5 ? 2 : 1, '#b8b8c4');
        }
        ctx.globalAlpha = 1;
      }
    });
  }
  // people walking along the quay
  for (const wk of WALKERS) {
    if (!here(wk.rank, crowd) || t < wk.t0 || t >= wk.t1) continue;
    const u = (t - wk.t0) / (wk.t1 - wk.t0), x = wk.dir > 0 ? -14 + u * (W + 28) : W + 14 - u * (W + 28), f = walkF(t);
    put(wk.y, () => {
      if (wk.pair) {
        person(x + 4, wk.y - 1, wk.P2, { view: 2, dir: wk.dir, step: f ^ 1, lean: frac(t / 9) < 0.3 ? 1 : 0 });
        const hy = person(x - 2, wk.y, wk.P, { view: 2, dir: wk.dir, step: f, aR: 4 });
        umbrella(x + 1, hy - 1, 17, wk.umb, -1);
      } else {
        const hy = person(x, wk.y, wk.P, { view: 2, dir: wk.dir, step: f, aR: 4 });
        umbrella(x + wk.dir, hy, 13, wk.umb, -wk.dir);
      }
    });
  }
  flushDL();
}
function flushDL() {
  DL.sort((a, b) => a.y - b.y);
  for (const q of DL) q.f();
}

/* ================= On the bridge: walkers behind the balustrade, a couple at the portico ================= */
const BW = [];
{
  const r = rng(808), periods = [40, 48, 60, 60, 80, 80, 120, 120, 240, 48];
  periods.forEach((p, i) => BW.push({ p, ph: r(), dir: i & 1 ? 1 : -1, top: TOPS[Math.floor(r() * TOPS.length)], hair: HAIRS[Math.floor(r() * 4)],
    rank: frac((i + 0.5) * 0.618), umb: r() < 0.4 ? ['#2a2a34', '#6a1a24', '#1a3a5a'][i % 3] : null }));
}
function drawBridgeWalkers(t, crowd) {
  for (const b of BW) {
    if (!here(b.rank, crowd)) continue;
    const u = frac(t / b.p + b.ph);
    if (u > 0.9) continue;
    const s = u / 0.9, x = Math.round(b.dir > 0 ? lerp(140, 360, s) : lerp(360, 140, s));
    if (x < 148 || x > 352) continue;
    const y = Math.round(deckY(x)) - 1, bob = walkF(t + b.ph) ? 0 : 1;
    R(x, y - 7 + bob, 2, 5, mixHex(b.top, NIGHT, 0.35)); R(x, y - 9 + bob, 2, 2, mixHex(b.hair, NIGHT, 0.2));
    R(x + (b.dir > 0 ? 1 : 0), y - 8 + bob, 1, 1, mixHex(SKINS[1], NIGHT, 0.3));
    if (b.umb) { R(x - 2, y - 12 + bob, 6, 1, b.umb); R(x - 1, y - 13 + bob, 4, 1, b.umb); R(x + 1, y - 11 + bob, 1, 2, '#1a1a20'); }
  }
  if (here(0.25, crowd)) {                                                   // the couple at the balustrade, a kiss every 40 s
    const u = frac(t / KISS_EVERY + 0.6), lean = u < 0.12 ? 1 : 0, y = Math.round(deckY(244)) - 1;
    R(241, y - 6, 2, 5, '#4a3a5a'); R(241 + lean, y - 8, 2, 2, '#3a2418');
    R(246, y - 6, 2, 5, '#8a3a3a'); R(246 - lean, y - 8, 2, 2, '#6c4222'); R(246 - lean, y - 7, 2, 2, '#6c4222');
    if (u > 0.5 && u < 0.6) R(244, y - 5, 1, 1, '#e2ac82');                  // holding hands
  }
}

/* ================= Things that pass: the gondolas rowing, the vaporetto ================= */
const GL = 194, VL = 186;                             // the gondolas' and the vaporetto's waterlines
const P_ROWER = [look(17, 1, 0, '#ece6da', BOTS[1], { stripes: '#1a2a5a', hat: '#d8c088', band: '#8a1a22', dk: 0.3, warm: 0.3 }),
  look(17, 2, 1, '#ece6da', BOTS[1], { stripes: '#7a1a22', hat: '#d8c088', band: '#1a2a5a', dk: 0.3, warm: 0.3 })];
function gondolaAt(g, t) {
  if (t < g.t0 || t >= g.t1) return null;
  const u = (t - g.t0) / (g.t1 - g.t0);
  return { x: g.dir < 0 ? lerp(W + 10, -60, u) : lerp(-60, W + 10, u), u };
}
function drawGondolas(t, crowd) {
  if (crowd <= 0) return;
  GONDOLAS.forEach((g, gi) => {
    const q = gondolaAt(g, t);
    if (!q) return;
    const len = 48, x = Math.round(q.x), dir = g.dir, wy = GL + Math.round(0.5 * wave(t, 60, gi * 0.3));
    const stern = dir > 0 ? x + 3 : x + len - 4, ph = frac(t / 4 + gi * 0.3), push = ph < 0.45 ? ph / 0.45 : 1 - (ph - 0.45) / 0.55;
    ctx.globalAlpha = 0.35; R(x + 2, wy + 1, len - 4, 2, '#000000'); ctx.globalAlpha = 1;          // the hull's dark reflection
    for (let k = 1; k <= 6; k++) { ctx.globalAlpha = 0.4 - k * 0.055; R(stern - dir * (k * 4), wy + 1 + (k >> 1), 3, 1, '#9ab8b0'); R(stern - dir * (k * 4), wy - (k >> 2), 1, 1, '#6a8a84'); }   // the wake
    ctx.globalAlpha = 1;
    ctx.drawImage(HULL_C[dir > 0 ? 1 : 0], x - 2, wy - 16);
    const mid = x + (len >> 1);                                                  // the passengers on the red seat
    R(mid - 5, wy - 5, 10, 2, '#5a1620');
    for (const [dx, col, hair] of g.lantern ? [[-1, TOPS[11], HAIRS[3]]] : [[-3, TOPS[0], HAIRS[0]], [2, TOPS[9], HAIRS[4]]]) {
      R(mid + dx, wy - 9, 3, 4, mixHex(col, NIGHT, 0.4)); R(mid + dx, wy - 11, 3, 2, mixHex(hair, NIGHT, 0.3));
    }
    if (!g.lantern) { const u = frac(t / 20); if (u < 0.3) { R(mid, wy - 11, 1, 1, '#ffe2a8'); halo(HL.phone, mid, wy - 11, 0.4); } }
    const hy = person(stern + dir, wy - 4, P_ROWER[gi], { view: 2, dir, aR: 3, lean: Math.round(push * dir) });
    const hx = stern + dir * (3 + Math.round(push * 2)), hyy = hy + 6;               // his hands, and the oar down into the water ahead
    const bx = stern + dir * (14 - Math.round(push * 6)), by = wy + 1;
    for (let k = 0; k <= 12; k++) R(Math.round(lerp(hx, bx, k / 12)), Math.round(lerp(hyy, by, k / 12)), 1, 1, '#8a6a42');
    R(bx - 1, by, 3, 1, push < 0.6 ? '#c8dcd8' : '#5a7a74');
    if (g.lantern) {
      const lx = dir > 0 ? x + len - 6 : x + 5;
      R(lx, wy - 9, 1, 4, '#1a1418'); R(lx - 1, wy - 12, 3, 3, '#ffd890'); halo(HL.lantern, lx, wy - 11, 0.85 + 0.15 * wave(t, 300));
      streak(ctx, t, lx, 2, '#ffc070', 0.4, 91, wy + 2, wy + 7);
    }
  });
}
function vapAt(v, t) {
  if (t < v.t0 || t >= v.t1) return null;
  const u = (t - v.t0) / (v.t1 - v.t0);
  return { x: v.dir > 0 ? lerp(-86, W + 8, u) : lerp(W + 8, -86, u), u };
}
function drawVaporetto(t, crowd) {
  for (const v of VAPS) {
    const q = vapAt(v, t);
    if (!q) continue;
    const L = 76, x = Math.round(q.x), d = v.dir, wl = VL + Math.round(0.4 * wave(t, 80)), f = i => (d > 0 ? x + i : x + L - 1 - i);   // i from the stern
    for (let k = 1; k <= 8; k++) { ctx.globalAlpha = 0.45 - k * 0.05; R(f(0) - d * k * 5, wl + (k & 1), 4, 1, '#b8d0c8'); }          // the wake
    ctx.globalAlpha = 0.3; R(Math.min(f(0), f(L - 1)), wl + 1, L, 3, '#000000'); ctx.globalAlpha = 1;
    for (let i = 0; i < L; i++) {
      const X = f(i), bowCut = i > L - 6 ? i - (L - 6) : 0;
      R(X, wl - 5 + Math.floor(bowCut / 2), 1, 5 - Math.floor(bowCut / 2), '#1a2028');                  // the dark hull
      R(X, wl - 6 + Math.floor(bowCut / 2), 1, 1, '#d8d4c8');                                            // its white rubbing strake
      if (i > 8 && i < L - 14) {                                                                          // the cabin
        const win = (i - 9) % 6 < 4;
        R(X, wl - 13, 1, 7, win ? '#f6e2a0' : '#d8d0c0');
        R(X, wl - 14, 1, 1, '#b8b4ac');
      }
      if ((i < 9 || (i >= L - 14 && i < L - 4)) && i % 3 === 0) R(X, wl - 9, 1, 3, '#a8a8a8');            // the railings of the open decks
    }
    for (let i = 4; i < L - 6; i++) R(f(i), wl - 9, 1, 1, i < 9 || i >= L - 14 ? '#c8c8c0' : '#d8d0c0');
    for (let i = L - 22; i < L - 15; i++) { R(f(i), wl - 18, 1, 4, i % 3 ? '#2a3440' : '#d8d4c8'); R(f(i), wl - 19, 1, 1, '#e8e4d8'); }   // the wheelhouse
    R(f(L - 18), wl - 22, 1, 3, '#2a2a30'); R(f(L - 18), wl - 23, 1, 1, '#ffffff');                       // the mast and its light
    R(f(L - 3), wl - 8, 1, 1, d > 0 ? '#60ff90' : '#ff5040');
    R(f(3), wl - 9, 2, 2, '#e88a2a');                                                                    // a life ring at the stern
    if (crowd > 0) for (let k = 0; k < 8; k++) {                                                          // passengers in the windows
      if (!here(frac((k + 0.5) * 0.618), crowd)) continue;
      const i = 9 + k * 6 + 1;
      R(f(i), wl - 11, 2, 3, '#4a3a3a'); R(f(i), wl - 12, 2, 1, '#2a1a1a');
    }
    const cx = f(L >> 1);
    halo(HL.cabin, cx, wl - 10, 0.8);
    for (let k = 0; k < 3; k++) streak(ctx, t, f(16 + k * 16), 4, '#f6d890', 0.32, 100 + k, wl + 2, wl + 14);
  }
}

/* ================= Birds: gulls on the poles and over the canal, pigeons on the quay and the roofs; the cat ================= */
const GULL = ['..hh..', 'wwwwhb', '.gggw.', '..l...'];
const GULL_KEY = { w: '#e8eaee', g: '#9aa0ac', h: '#f4f4f6', b: '#e8b040', l: '#c88a4a' };
const GULL_FLY = [['g....g', '.gwwg.', '..ww..'], ['......', 'ggwwgg', '..ww..'], ['......', '.gwwg.', 'g.ww.g']];
let GULL_C, PIG, CAT_C;
const PERCHED = [[50, 1], [430, 0]];                  // gulls standing on palina tops (x, facing left?)
const LAND_POLE = 437;
const paloTop = x => 148 - (x % 3) * 3 - 2;           // the row the knob's top sits on
function drawFlyingGull(x, y, f, big) {
  const X = Math.round(x), Y = Math.round(y);
  GULL_FLY[f].forEach((row, j) => { for (let i = 0; i < 6; i++) if (row[i] !== '.') R(X - 3 + i, Y - 1 + j, big && row[i] === 'g' ? 1 : 1, 1, row[i] === 'w' ? '#e8eaee' : '#8a909c'); });
}
function drawGulls(t) {
  for (const [x, fl] of PERCHED) {
    const look = hash(x, step(t, 120), 2) < 0.3;
    ctx.drawImage(GULL_C[look ? 1 - fl : fl], x - 2, paloTop(x) - 4);
  }
  const [a, b] = GULL_DROP, top = paloTop(LAND_POLE);
  if (t >= a && t < b) {                                                       // one glides down and lands
    const u = (t - a) / (b - a), e = 1 - (1 - u) ** 2;
    drawFlyingGull(lerp(300, LAND_POLE + 1, e), lerp(70, top - 3, e), u > 0.85 ? 0 : Math.floor(t * 6) % 3);
  } else if (t >= b && t < 228) ctx.drawImage(GULL_C[0], LAND_POLE - 2, top - 4);
  else if (t >= 228 && t < 238) { const u = (t - 228) / 10; drawFlyingGull(lerp(LAND_POLE + 1, 520, u), top - 3 - u * 60, Math.floor(t * 6) % 3); }
  for (const [g0, g1, d] of GULL_PASS) {                                       // a pair crossing high over the canal
    if (t < g0 || t >= g1) continue;
    const u = (t - g0) / (g1 - g0);
    for (let k = 0; k < 2; k++) {
      const x = d > 0 ? lerp(-20, W + 20, u - k * 0.04) : lerp(W + 20, -20, u - k * 0.04), y = 46 + k * 7 + 6 * Math.sin(u * TAU + k);
      drawFlyingGull(x, y, hash(k, step(t, 480), 3) < 0.5 ? 1 : Math.floor(t * 5 + k) % 3);
    }
  }
}
const PIGEON = {
  up: ['.....HH', '....HHB', '.GDDGN.', 'GGDDGG.', '..L.L..'],
  peck: ['.......', '.GDDG..', 'GGDDGNH', 'GGGGGHB', '..L.L..'],
  walk: ['.....HH', '....HHB', '.GDDGN.', 'GGDDGG.', '.L...L.'],
};
const PIG_COL = { G: '#5c5c70', D: '#3c3c50', H: '#6c6c84', N: '#4a6e62', B: '#a0846a', L: '#904040' };
const QUAY_PIGEONS = [[214, 24, 244, 3, 0.1], [262, 30, 252, 2, 0.45], [318, 20, 240, 3, 0.7], [180, 16, 258, 2, 0.25]];   // cx, reach, feet row, trips, phase
function drawQuayPigeons(t, crowd) {
  QUAY_PIGEONS.forEach(([cx, ax, fy, k, ph], i) => {
    let x = cx + ax * wave(t, k, ph), y = fy, vel = Math.cos(TAU * (k * t / LOOP + ph)), hop = 0;
    for (const wk of WALKERS) {                                                // someone walks by: they hop aside with a flap
      if (!here(wk.rank, crowd) || t < wk.t0 || t >= wk.t1) continue;
      const u = (t - wk.t0) / (wk.t1 - wk.t0), wx = wk.dir > 0 ? -14 + u * (W + 28) : W + 14 - u * (W + 28);
      const dd = Math.abs(wx - x);
      if (dd < 16 && Math.abs(wk.y - y) < 18) { hop = Math.round(Math.sin((1 - dd / 16) * Math.PI) * 4); vel = wk.dir; x += wk.dir * (16 - dd) * 0.6; }
    }
    const moving = Math.abs(vel) > 0.35;
    const pose = hop ? 'up' : moving ? (step(t, 1440) & 1 ? 'walk' : 'up') : hash(i, step(t, 480), 3) < 0.55 ? 'peck' : 'up';
    ctx.drawImage(PIG[pose][vel < 0 ? 1 : 0], Math.round(x) - 3, y - 4 - hop);
    if (hop > 1) { R(Math.round(x) - 3, y - 4 - hop, 2, 1, '#8a8aa0'); R(Math.round(x) + 2, y - 4 - hop, 2, 1, '#8a8aa0'); }
  });
}
const ROOF_PERCH = [[104, 97], [112, 97], [122, 97], [136, 97], [44, 67], [54, 67], [82, 79], [90, 79], [366, 91], [384, 91], [404, 91], [440, 55]];
const FLOCK = ROOF_PERCH.map(([x, y], i) => {
  const r = rng(900 + i);
  return { x, y, i, laps: 1 + (r() < 0.45 ? 1 : 0), phi: r() * TAU, rx: 18 + r() * 30, ry: 8 + r() * 12, bx: (r() < 0.5 ? -1 : 1) * (14 + r() * 40), by: 10 + r() * 22, del: r() * 0.06 };
});
const FLAP = [['1...1', '.121.', '.....'], ['.....', '11211', '.....'], ['.....', '.121.', '1...1']];
function drawFlock(t) {
  const flying = t >= FLOCK_T0 && t < FLOCK_T1;
  for (const b of FLOCK) {
    const s = flying ? clamp(((t - FLOCK_T0) / (FLOCK_T1 - FLOCK_T0) - b.del) / (1 - 2 * b.del), 0, 1) : 0;
    if (s <= 0 || s >= 1) { R(b.x, b.y, 2, 1, '#3a3a4a'); R(b.x + (hash(b.i, step(t, 240), 8) < 0.5 ? 0 : 1), b.y - 1, 1, 1, '#6a6a7c'); continue; }
    const e = s < 0.5 ? Math.sqrt(Math.sin(Math.PI * s)) : Math.pow(Math.sin(Math.PI * s), 1.5), th = b.phi + TAU * b.laps * s;
    const x = Math.round(b.x + (b.rx * (Math.cos(th) - Math.cos(b.phi)) + b.bx) * e), y = Math.round(b.y + (b.ry * (Math.sin(th) - Math.sin(b.phi)) - b.by) * e);
    const glide = s > 0.15 && s < 0.85 && hash(b.i, step(t, 480), 9) < 0.3;
    FLAP[glide ? 1 : (Math.floor(t * 9 + b.i * 0.7) % 3)].forEach((row, j) => { for (let i = 0; i < 5; i++) if (row[i] !== '.') R(x - 2 + i, y - 1 + j, 1, 1, row[i] === '2' ? '#4a4a58' : '#b8b8c4'); });
  }
}
const CAT_SIT = ['k..k...', 'kkkk...', 'kwkk...', '.kkkk..', '.kkkkk.', '.kkkkk.', 'kkkkkk.'];
const CAT_WALK_F = [['.......kk', 'k.....kkk', '.kkkkkkk.', '.kkkkkk..', '.k.k.k.k.'], ['.......kk', '.k....kkk', '.kkkkkkk.', '.kkkkkk..', '..kk.kk..']];
const CAT_KEY = { k: '#0e0c14', w: '#3a3448' };
const CAT_Y = RIVA + 6;
function drawCat(t) {
  const A = 152, B = 266;
  for (const [a, b, d] of CAT_WALK) if (t >= a && t < b) {
    const u = (t - a) / (b - a), x = Math.round(d > 0 ? lerp(A, B, u) : lerp(B, A, u));
    ctx.drawImage(CAT_C.walk[d > 0 ? 0 : 1][Math.floor(t * 4) & 1], x - 4, CAT_Y - 5);
    return;
  }
  const x = t >= CAT_WALK[0][1] && t < CAT_WALK[1][0] ? B : A, look = hash(9, step(t, 60), 3) < 0.3;
  ctx.drawImage(CAT_C.sit, x - 3, CAT_Y - 7);
  const ts = wave(t, 30, 0.2) * 1.2 + wave(t, 97, 0.5) * 0.5;               // the tail swishes on the stone
  for (let j = 0; j < 5; j++) R(x + 3 + j, CAT_Y - 1 - Math.round(Math.max(0, ts) * j / 3), 1, 1, '#0e0c14');
  if (look && frac(t / 4) < 0.92) { R(x - 3, CAT_Y - 5, 1, 1, '#d6e47a'); R(x - 1, CAT_Y - 5, 1, 1, '#d6e47a'); }
}

/* ================= Small motions: the lion flag, the laundry on the altana, the chandeliers ================= */
function drawFlag(t) {
  for (let k = 0; k < 10; k++) {
    const off = Math.round(Math.sin(TAU * (9 * t / LOOP) - k * 0.6) * (k / 9) * 1.4 + wave(t, 31, k * 0.05) * 0.4 * (k / 9));
    for (let j = 0; j < 6; j++) {
      const lion = k >= 3 && k <= 6 && j >= 1 && j <= 4 && !(k === 6 && j === 1) && !(k === 3 && j === 4);
      R(FLAG.x + 1 + k, FLAG.y + j + off, 1, 1, lion ? (j === 1 ? '#f0c860' : '#d4a24c') : k === 9 ? '#6a1018' : '#8a1a22');
    }
  }
}
const LAUNDRY = [[367, 5, 6, '#d8d4cc', 1], [375, 3, 7, '#3a6ac8', 0], [383, 5, 5, '#b8323a', 1], [392, 7, 6, '#e0d8c0', 0]];   // x, w, h, colour, a shirt?
const lineY = x => 75 + Math.round(2.5 * Math.sin(Math.PI * (x - 363) / 41));
function drawLaundry(t) {
  for (let x = 363; x <= 404; x++) R(x, lineY(x), 1, 1, '#2a2226');
  LAUNDRY.forEach(([x, w, h, col, shirt], i) => {
    const y0 = lineY(x + (w >> 1)) + 1;
    for (let j = 0; j < h; j++) {
      const off = Math.round((wave(t, 18 + i * 5, i * 0.3) * 0.8 + wave(t, 47, i * 0.2) * 0.4) * j / h * 2);
      const ww = shirt && j < 2 ? w + 2 : w, xo = shirt && j < 2 ? -1 : 0;
      R(x + off + xo, y0 + j, ww, 1, mixHex(col, NIGHT, 0.45 + j * 0.03));
    }
    R(x, y0 - 1, 1, 1, '#c8b890'); R(x + w - 1, y0 - 1, 1, 1, '#c8b890');                        // pegs
  });
}
function drawWindows(t, layer) {
  for (const w of WINS) {
    if (w.layer !== layer || !winLit(w, t)) continue;
    ctx.fillStyle = w.col;
    if (w.rows) for (let j = 0; j < w.rows.length; j++) ctx.fillRect(w.x + w.rows[j][0], w.y + j, w.rows[j][1], 1);
    else for (let q = 0; q < w.poly; q++) { const rs = pointed(w.lw, w.h); for (let j = 0; j < rs.length; j++) ctx.fillRect(w.x + q * (w.lw + w.gap) + rs[j][0], w.y + j, rs[j][1], 1); }
    if (layer >= 2) halo(HL.win, w.x + (w.w >> 1), w.y + (w.h >> 1), layer === 2 && !w.rows ? 1 : 0.6);
  }
}
const winLit = (w, t) => hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 5) < w.p;
function drawChandeliers(t) {
  for (const c of CHANDELIERS) {
    if (!winLit(WINS[c.win], t)) continue;
    const tw = 0.7 + 0.3 * wave(t, 90 + c.x % 17, c.x * 0.01);
    ctx.globalAlpha = tw;
    R(c.x - 1, c.y, 3, 1, '#fff6d8'); R(c.x - 2, c.y + 1, 1, 1, '#ffe8a8'); R(c.x + 2, c.y + 1, 1, 1, '#ffe8a8'); R(c.x, c.y + 1, 1, 2, '#fff6d8');
    ctx.globalAlpha = 1;
    halo(HL.chand, c.x, c.y + 1, 0.5 * tw);
  }
}

/* ================= Per-frame: the sign on the altana ================= */
let ADS, PITCH, SIGN_C;
const BILL_FLICK = [[88, 89.4], [201, 202.2]];
function drawBillboard(t, pitch) {
  const lv = flick(t, BILL_FLICK, 13);
  halo(HL.billGlow, BILL.x + 24, BILL.y + 6, (0.9 + 0.1 * wave(t, 8, 0.2)) * (2 + lv) / 3);
  ctx.drawImage(SIGN_C, 0, 0);
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(ADS[0], BILL.x, BILL.y);
    if (lv < 1) { ctx.globalAlpha = 1 - lv; ctx.drawImage(ADS[1], BILL.x, BILL.y); ctx.globalAlpha = 1; }
    for (let k = 0; k < 3; k++) {                                             // bubbles rising in the spritz
      const { u } = cycle(t, 80 + k * 20, k / 3);
      R(BILL.x + 6 + (k % 2) * 2, BILL.y + 9 - Math.floor(u * 4), 1, 1, '#ffe2b0');
    }
    const g = frac(t / 8);                                                     // a glint runs along the gold rule
    if (g < 0.3) { ctx.globalAlpha = Math.sin(Math.PI * g / 0.3); R(BILL.x + 17 + Math.floor(g / 0.3 * 22), BILL.y + 8, 2, 1, '#fff2c0'); ctx.globalAlpha = 1; }
  }
  PITCH.draw(ctx, t, BILL.x, BILL.y, pitch);
  BILL_LAMPS.forEach((lx, i) => {
    const a = i === 1 ? lv : 1;
    if (a < 1) R(lx - 1, BILL_LAMP_Y + 1, 3, 1, `rgba(40,26,18,${(0.85 * (1 - a)).toFixed(3)})`);
    halo(HL.billLamp, lx, BILL_LAMP_Y + 2, a * (0.92 + 0.08 * wave(t, 700 + i * 37)));
  });
}

/* ================= Prerendered state ================= */
let HL, SKY_C, MOON_C, CLOUDS, MISTS, FAR_C, MID_C, BACK_C, FRONT_C, WARM_C, WATER_C, NEAR_C, PALINE_C, QUAY_C, BAR_C, LAMPS_C, VIG_C;
function renderVignette() {
  const p = pix(W, H), c = [6, 8, 18];
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.72, 1.42, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, c, Math.round(q * 0.45 * 255));
  }
  return p.done();
}
function renderMid() {                                 // the campanile, the Camerlenghi and the Fondaco
  const p = pix(W, WL + 1), r = rng(55);
  drawCampanile(p); drawCamerlenghi(p, r); drawFondaco(p, r);
  return p.done();
}
function prerender() {
  WINS.length = 0; CHANDELIERS.length = 0; FAR_GLINTS.length = 0;
  HL = {
    moon: makeHalo(70, '#9aa4c8', 0.2, 1.5), moonCore: makeHalo(18, '#f0ecd8', 0.34, 1.4), star: makeHalo(3, '#dfe6ff', 0.5, 1),
    bridge: makeHalo(96, '#ffb064', 0.16, 1.6), bridgeLow: makeHalo(60, '#ffc884', 0.18, 1.6), portico: makeHalo(18, '#ffd08a', 0.4, 1.4),
    lantern: makeHalo(8, '#ffe0a0', 0.5, 1.3), camer: makeHalo(44, '#ffb868', 0.14, 1.6), fond: makeHalo(40, '#ffb060', 0.12, 1.6),
    lamp: makeHalo(54, '#ffbe70', 0.24, 1.7), lampCore: makeHalo(11, '#fff0c8', 0.4, 1.3), bulb: makeHalo(6, '#ffd890', 0.45, 1.2),
    awning: makeHalo(50, '#ffb868', 0.18, 1.6), door: makeHalo(30, '#ffb060', 0.24, 1.5), win: makeHalo(6, '#ffb45a', 0.16),
    chand: makeHalo(8, '#ffe8b0', 0.4, 1.3), phone: makeHalo(5, '#bfe8ff', 0.4, 1.2), cabin: makeHalo(40, '#ffe0a0', 0.2, 1.5),
    billGlow: makeHalo(34, '#ffd8a0', 0.14, 1.6), billLamp: makeHalo(6, '#ffe8b8', 0.5, 1.3), bell: makeHalo(22, '#ffd890', 0.3, 1.5),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer); MISTS = MIST_DEFS.map(makeMist);
  FAR_C = renderFar(); MID_C = renderMid();
  BACK_C = renderBridgeBack(); FRONT_C = renderBridgeFront(); WARM_C = renderBridgeWarm(BACK_C, FRONT_C);
  WATER_C = renderWater(); ARCH_VEIL_C = renderArchVeil(); NEAR_C = renderNear(); PALINE_C = renderPaline();
  QUAY_C = renderQuay(); BAR_C = renderBar(); LAMPS_C = renderLamps(); VIG_C = renderVignette();
  renderStazio();
  ADS = [renderAd(false), renderAd(true)]; SIGN_C = renderSign(); PITCH = makePitch(BILL.w, BILL.h, { accent: '#d4a24c' });
  PIG = {}; for (const k in PIGEON) PIG[k] = [sprite(PIGEON[k], PIG_COL), sprite(PIGEON[k], PIG_COL, true)];
  CAT_C = { sit: sprite(CAT_SIT, CAT_KEY), walk: [0, 1].map(fl => CAT_WALK_F.map(rows => sprite(rows, CAT_KEY, !!fl))) };
  GULL_C = [sprite(GULL, GULL_KEY, true), sprite(GULL, GULL_KEY)];
  STREAKS.length = 0;
  STREAKS.push([BR.cx, 7, '#ffc870', 0.42, -1, WL + 1, WL + 36]);
  for (const [x] of BR_LANTERNS.slice(2)) STREAKS.push([x, 2, '#ffd890', 0.4, -1, WL + 1, WL + 24]);
  for (let k = 0; k < 5; k++) STREAKS.push([FOND.x0 + 10 + k * 12, 3, '#e8b060', 0.26, -1, WL + 1, WL + 16]);
  for (const c of CHANDELIERS) STREAKS.push([c.x, 6, '#ffcf7a', 0.3, c.win, NWL + 1, NWL + 26]);
  for (const w of WINS) if (w.layer === 4) {                                   // the arcade's shops, from their mirror image down
    const y0 = Math.round(WL + (WL - w.y - w.h) / 1.35);
    STREAKS.push([w.x + (w.w >> 1), 2, '#ffc870', 0.26, w.id, y0, y0 + 16]);
  }
}

/* ================= Per-frame: the sky and the far city ================= */
function drawScroll(cv, y, k, t, dir = 1) {
  let off = scroll(t, k, W);
  if (dir < 0) off = (W - off) % W;
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.5 + 0.5 * wave(t, s.k, s.ph)); R(s.x, s.y, 1, 1, '#d6deff'); }
  ctx.globalAlpha = 1;
  const veil = 0.75 + 0.25 * wave(t, 3, 0.1);                                 // the mist thins and thickens over the moon
  halo(HL.moon, MOON.x, MOON.y, veil); halo(HL.moonCore, MOON.x, MOON.y, 0.8);
  ctx.globalAlpha = 0.65 + 0.35 * (1 - veil); ctx.drawImage(MOON_C, MOON.x - 7, MOON.y - 7); ctx.globalAlpha = 1;
  drawScroll(CLOUDS[0].c, CLOUDS[0].y, CLOUDS[0].k, t);
  drawScroll(CLOUDS[1].c, CLOUDS[1].y, CLOUDS[1].k, t, -1);
  drawScroll(CLOUDS[2].c, CLOUDS[2].y, CLOUDS[2].k, t);
}
function drawMist(f, t, breathe) {
  ctx.globalAlpha = f.alpha * (0.75 + 0.25 * wave(t, breathe, f.seed * 0.1));
  drawScroll(f.c, f.y, f.k, t, f.dir);
  ctx.globalAlpha = 1;
}
const bellLevel = t => { let b = 0; for (const tb of BELLS) { const dt = t - tb; if (dt >= 0 && dt < 3.5) b = Math.max(b, Math.exp(-dt * 1.1) * (1 - dt / 3.5)); } return b; };
function drawMid(t, billboard, pitch) {
  halo(HL.camer, 128, 128, 0.9); halo(HL.fond, 372, 126, 0.8);
  ctx.drawImage(MID_C, 0, 0);
  drawWindows(t, 1);
  const b = bellLevel(t);                                                      // a stroke: the belfry glows, the sound spreads
  if (b > 0.02) {
    ctx.globalAlpha = b; for (const [bx, by, bw, bh] of BELFRY) R(bx, by + 1, bw, bh - 1, '#ffb858'); ctx.globalAlpha = 1;
    halo(HL.bell, CAMP.x0 + 4, 66, b);
  }
  for (const tb of BELLS) {
    const dt = t - tb;
    if (dt < 0 || dt > 2.8) continue;
    const rr = 5 + dt * 26, n = Math.floor(rr * 2);
    ctx.globalCompositeOperation = 'lighter'; ctx.fillStyle = '#ffd890'; ctx.globalAlpha = (1 - dt / 2.8) * 0.32;
    for (let j = 0; j < n; j++) { const a = j / n * TAU; ctx.fillRect(Math.round(CAMP.x0 + 4 + Math.cos(a) * rr), Math.round(66 + Math.sin(a) * rr * 0.8), 1, 1); }
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  }
  drawFlag(t);
  drawLaundry(t);
  if (billboard) drawBillboard(t, pitch);
}
function drawBridge(t, crowd) {
  const warm = 0.5 + 0.5 * wave(t, 2, 0.75);                                  // the floodlights' slow breath, twice a loop
  halo(HL.bridge, BR.cx, 128, 0.75 + 0.25 * warm); halo(HL.bridgeLow, BR.cx, 150, 0.7 + 0.3 * warm);
  ctx.save(); ctx.beginPath(); ctx.rect(CAMER.x1, 0, FOND.x0 - CAMER.x1, WL + 1); ctx.clip();   // the ramps' ends, and whoever walks them, go in behind the two palazzi
  ctx.drawImage(BACK_C, 0, 0);
  drawWindows(t, 4);
  drawBridgeWalkers(t, crowd);
  ctx.drawImage(FRONT_C, 0, 0);
  ctx.globalAlpha = 0.1 + 0.3 * warm; ctx.drawImage(WARM_C, 0, 0); ctx.globalAlpha = 1;
  ctx.restore();
  halo(HL.portico, BR.cx, 108, 0.8 + 0.2 * warm);
  BR_LANTERNS.forEach(([x, y], i) => {
    const a = 0.85 + 0.15 * wave(t, 900 + i * 61, i * 0.3);
    if (i >= 2) { R(x, y, 1, 5, '#2a2228'); R(x - 1, y - 3, 3, 3, '#2a2228'); R(x, y - 2, 1, 1, '#ffe8b0'); }
    halo(HL.lantern, x, y - 2, a);
  });
}

/* ================= Per-frame: the quay ================= */
function drawQuay(t) {
  ctx.drawImage(QUAY_C, 0, RIVA);
  const lv = flick(t, LAMP_FLICK, 21);
  LAMPS.forEach((x, i) => streak(ctx, t, x + 1, 4, '#ffcf80', 0.22 * (i ? 1 : lv), 50 + i, EDGE - 6, H - 6));   // the lamps in the wet stone
  streak(ctx, t, (AWN.x0 + AWN.x1) >> 1, 40, '#ffb868', 0.12, 58, TROW + 2, H - 4);
}
function drawBar(t) {
  halo(HL.awning, (AWN.x0 + AWN.x1) >> 1, AWN.y + 30, 0.85 + 0.1 * wave(t, 13));
  halo(HL.door, BAR.door + 7, 228, 0.9 + 0.1 * wave(t, 23, 0.4));
  blit(BAR_C);
  drawWindows(t, 3);
  const cf = 0.75 + 0.25 * wave(t, 600, 0.2) * wave(t, 230, 0.6);                 // the shrine's candle
  R(SHRINE.x, SHRINE.y + 10, 1, 2, '#e8dcc0'); R(SHRINE.x, SHRINE.y + 9, 1, 1, cf > 0.8 ? '#fff0b0' : '#ffc060');
  halo(HL.bulb, SHRINE.x, SHRINE.y + 9, cf);
  AWN_BULBS.forEach(([x, y], i) => {
    const sw = Math.round(wave(t, 16, i * 0.11) * 0.6);
    R(x + sw, y, 1, 2, ['#ffe9a0', '#ffc468', '#ffd0a0'][i % 3]);
    halo(HL.bulb, x + sw, y + 1, 0.75 + 0.25 * wave(t, 400 + i * 37, i * 0.3));
  });
}
function drawLamps(t) {
  const lv = flick(t, LAMP_FLICK, 21);
  ctx.drawImage(LAMPS_C, 0, 0);
  LAMPS.forEach((x, i) => {
    const a = (i === 0 ? lv : 1) * (0.94 + 0.06 * wave(t, 1100 + i * 90, i * 0.3));
    if (a < 0.9) R(x - 1, LAMP_Y - 5, 4, 9, `rgba(60,36,20,${(0.9 - a).toFixed(3)})`);
    halo(HL.lampCore, x + 1, LAMP_Y - 1, a);
    halo(HL.lamp, x + 1, LAMP_Y + 4, 0.9 * a);
  });
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the site's face on the sign (promo captures, the website); billboard: false leaves the sign off the altana
// (the loop videos); weather: a WEATHERS pick (site/js/engine/weather.js), the city's own when left out;
// crowd: the share of the people to show, 0..1 (CROWD when left out or not a number); 0 = nobody at all
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, lights = billboard ? RAIN_LIGHTS_BB : RAIN_LIGHTS;
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  R(0, SKY_H, W, H - SKY_H, '#0c1418');
  drawSky(t);
  if (wx.drizzle) drawDrizzle(DRIZZLE[0], t, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  ctx.drawImage(FAR_C, 0, 0); drawWindows(t, 0);
  drawMist(MISTS[0], t, 3);
  drawMid(t, billboard, pitch);
  drawBridge(t, crowd);
  drawWaterFar(t);
  ctx.drawImage(NEAR_C, 0, 0); drawWindows(t, 2); drawChandeliers(t);
  drawFlock(t);
  drawWaterNear(t, wx.drizzle);
  drawPaline(t);
  drawGulls(t);
  drawMist(MISTS[1], t, 4);
  drawVaporetto(t, crowd);
  drawGondolas(t, crowd);
  if (wx.drizzle) drawDrizzle(DRIZZLE[1], t, lights);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawStazio(t);
  drawMist(MISTS[2], t, 5);
  drawQuay(t);
  drawPuddles(t, wx.drizzle);
  drawBar(t);
  drawLamps(t);
  drawCat(t);
  drawQuayPigeons(t, crowd);
  drawCast(t, crowd);
  if (wx.drizzle) drawDrizzle(DRIZZLE[2], t, lights);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'venice',
  name: 'Venice',
  tagline: 'Mist over the Grand Canal',
  timeZone: 'Europe/Rome',
  accent: '#d4a24c',                                   // Venetian gold, written out: tools/seo/seo.mjs reads it from the source
  focusX: 250,
  landmark: [146, 354],                                // the Rialto Bridge, ramp foot to ramp foot
  landmarkTop: 83,                                     // the row of the portico's pediment
  billboard: BILL,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  ambience: { water: 0.5, rain: 0.3, crowd: 0.12 },
  events: [
    { t: VAPS[0].t0 + 0.5, type: 'horn', x: 30, gain: 0.45 },
    { t: VAPS[0].t0, type: 'boat', dur: VAPS[0].t1 - VAPS[0].t0, pan: [-1, 1], gain: 0.25 },
    { t: GULL_PASS[0][0] + 4, type: 'gull', x: 200, gain: 0.35 },
    { t: GULL_DROP[0] + 4, type: 'gull', x: LAND_POLE, gain: 0.4 },
    ...BELLS.map(t => ({ t, type: 'bell', x: CAMP.x0 + 4 })),
    { t: VAPS[1].t0 + 0.5, type: 'horn', x: 450, gain: 0.45 },
    { t: VAPS[1].t0, type: 'boat', dur: VAPS[1].t1 - VAPS[1].t0, pan: [1, -1], gain: 0.25 },
    { t: GULL_PASS[1][0] + 4, type: 'gull', x: 280, gain: 0.35 },
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
